import { ShareTarget } from '../../services/sharing/ShareTarget'
/**
 * Left column — the project sidebar.
 *
 * ONE VIEW (RFC 0280 S1), two sections in a fixed order:
 *
 *   the working set  everything you have open, most recent first. Four kinds of
 *                    thing: files, apps, WEB PAGES, and the SESSIONS you are in.
 *                    A working set split across three surfaces (files here, pages
 *                    in a stray window, sessions only in the agent panel) is not a
 *                    working set — you cannot see what you have going without
 *                    looking in three places. Each row goes back to where that
 *                    kind of thing lives: files and pages to the canvas, sessions
 *                    to the agent panel.
 *   the directory    the full project, as a FOLDER TREE (see fileTree.ts).
 *
 * THE SWITCH IS GONE, and so is the apps view. Three views chosen from a footer
 * made the two things a person alternates between fastest — the working set and
 * the tree — mutually exclusive: every trip to the tree lost the list, and every
 * trip back lost your place in the tree. The apps view spent a third of that
 * switch on five rows that RFC 0273 (the panel column) and RFC 0278 (header
 * items) already show, and it could only ever say what was INSTALLED, never what
 * exists. Installed apps move to the apps modal (RFC 0280 S3).
 *
 * ONE SCROLL under the header. Everything below the header row (Spaces, Open,
 * Files, Code) shares a single scrollbar. The first stacked shape put Apps above
 * Files in one scroll and failed because Apps was a list nobody browsed; the
 * fix for that was a bounded top section with its own scroller, which in use
 * meant two scrollbars in a 240px column, a working set that could not be
 * scrolled out of the way, and Files and Code squeezed into what was left.
 * With apps gone, one column reads top to bottom like a document.
 *
 * SPACES sit above the working set (RFC 0281 §7). RFC 0280's groups are gone:
 * a group was a hand made list of ids beside the working set and a space is a
 * named arrangement you can switch to that already holds ids, so keeping both
 * made filing something and opening it two acts with two homes.
 *
 * Source of truth is the project doc's DOCUMENT CATALOG (RFC 0263):
 * `projectDocuments` over the project doc, so rendering hydrates ZERO document
 * docs (the catalog rows carry the metadata) and every row is live — a document
 * the agent creates mid-session appears as the registry syncs. RFC 0264's
 * checkout files will union into the tree later; `onOpen` is theirs too.
 *
 * Two presentations: pinned = a square column with a collapse control; floating
 * = a rounded, shadowed overlay (shown on left-edge hover while collapsed) with
 * a pin control to make it a column again.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  addProjectSource,
  addProjectToWorld,
  adoptCode,
  createFolder,
  createFolderInWorld,
  createProject,
  deleteDocument,
  detachProjectSource,
  dissolveProject,
  documentAsFile,
  documentLeftForFile,
  ensureStandingDocument,
  fileAsDocument,
  fileNotTaken,
  folderAsFiles,
  folderLeftForFiles,
  freeDocumentPath,
  isAssumedDefaultSource,
  type MoveTarget,
  makeProject,
  moveDocument,
  moveFolder,
  moveFolderWithProjects,
  moveSourceToProject,
  moveToProject,
  normalizeDocumentPath,
  noteSessionBranch,
  projectSessionCode,
  projectSources,
  projectString,
  projectWorldSkills,
  removeEmptyFolder,
  removeEmptyFolderInWorld,
  removeProjectFromWorld,
  renameDocument,
  resolveProjectMove,
  restoreDocument,
  SessionTaskLog,
  STANDING_TITLE,
  setProjectParent,
  setProjectSourceFolder,
  standingDocIdOf,
  worldProjectMounts,
} from '@mod/core/model'
import type { SpaceRef } from '@mod/core/model/spaces'
import { createSharingProject } from '@mod/core/sharing'
import { TaskChip, type TaskElementTask, type TaskPort } from '@mod/sdk/ui'
import {
  AppWindow,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Columns2,
  Compass,
  FileCode,
  FileText,
  Globe,
  MoreHorizontal,
  PanelLeftClose,
  Pin,
  Plus,
  ScrollText,
  Sparkles,
  X,
} from 'lucide-react'
import {
  Fragment,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { BranchChip } from '../../features/branches/BranchChip'
import { branchPaneOf } from '../../features/branches/branchPane'
import {
  CODE_TREE_CHANGED_EVENT,
  type CodeDropTarget,
  type CodeMoveTarget,
  CodeTreeSection,
  type CodeTreeSectionProps,
  type ListCodeBranches,
  type ListCodeDir,
} from '../../features/code/CodeTreeSection'
import {
  buildWorldDirectory,
  type SidebarSkill,
  type TreeNode,
} from '../../features/code/fileTree'
import {
  historyProjectOf,
  historySubject,
} from '../../features/projectHistory/historyPane'
import { canEmbedWebPages } from '../../features/web/webPage'
import { fetchVcsFromServer } from '../../sdk/vcs'
import { AppIcon } from '../../services/apps/AppIcon'
import { ABSENT, contentHash } from '../../services/code/fileDraft'
import {
  chooseProjectFolder,
  chooseSourceFolder,
  PROJECT_FOLDER_EVENT,
  type ProjectFolder,
  readProjectFolder,
  unmountProjectSource,
} from '../../services/code/projectFolder'
import { useLocalPersonId } from '../../services/identity/localPerson'
import { SessionMark } from '../../services/sessions/SessionMark'
import { appSkillOf, isModSkill } from '../../services/skills/skillCatalog'
import { useSkills } from '../../services/skills/useSkills'
import { projectTaskCount, registryTasks } from '../../services/tasks/registry'
import { useOptionalTabRepoBoot } from '../../services/world/repo/TabRepoProvider'
import { useDocument } from '../../services/world/repo/useDocument'
import { useWorldProjects } from '../../services/world/useWorldProjects'
import { getOrCreateWorld, readWorldPointer } from '../../services/world/world'
import { worldPath } from '../../services/world/worldUrl'
import { SpaceDot } from '../spaces/SpaceDot'
import {
  type SpaceActivity,
  spaceItemIds,
  spacePaneCount,
} from '../spaces/spaceRows'
import { useProjectDocs } from '../spaces/useProjectCatalogs'
import { useSpaceActions } from '../spaces/useSpaceActions'
import { useSpaceReorder } from '../spaces/useSpaceReorder'
import {
  baseOf,
  canDropDocument,
  canMoveFolder,
  canPlaceSourceAt,
  carryFolderKeys,
  documentTarget,
  folderTarget,
  freeDocumentTarget,
  freeFolderTarget,
  parentOf,
  renamedFolder,
  sourcePlacementClash,
} from './folderMoves'
import { Panel, PanelAction } from './Panel'
import { useSharingAccountWorld } from './SharingAccountGate'
import {
  appAsFile,
  docAsFile,
  type HomeItem,
  partitionSidebarItems,
  projectSessions,
  resolveHomeItems,
} from './sidebarItems'
import type { Subject } from './subject'

/**
 * What a dragged sidebar row carries.
 *
 * A private mime rather than `text/plain`, so a drag out of the sidebar into a
 * text field does not paste a document id, and a drag of something else into
 * the sidebar reads as nothing rather than as an id that resolves to nothing.
 */
const DRAG_MIME = 'application/x-mod-sidebar-item'

/**
 * What a Documents-tree row carries when dragged to another folder (RFC 0338
 * 3c). Its own mime, so a tree row dropped on a working-set group, or a
 * working-set row dropped on a folder, is a drop neither side recognises.
 */
const MOVE_MIME = 'application/x-mod-sidebar-move'

/** What a move may say when it lands where something already is (RFC 0357
 *  3i). `merge` is a folder's answer only; every kind of row can rename. */
type MoveOpts = {
  merge?: boolean
  /** A drop into a checkout: a taken file name moves on to a free one. */
  rename?: boolean
  /** Called instead of a refusal when core says a path is taken there. A
   *  checkout, whose files the window does not hold, says where too. */
  onCollision?: (
    message: string,
    at?: { into: string; renameTo: string },
  ) => void
}

/** A checkout already has a file at `into` (RFC 0357 3i). Said as a
 *  collision, with the free name beside it, so the drop can ask. */
class CheckoutCollision extends Error {
  constructor(
    readonly into: string,
    readonly renameTo: string,
  ) {
    super(`${into} is already taken in the checkout`)
  }
}

/** A drop into a checkout that failed: asked when it was a taken name,
 *  said otherwise. */
function refusedOrCheckoutCollision(
  what: string,
  error: unknown,
  opts?: MoveOpts,
): void {
  if (error instanceof CheckoutCollision && opts?.onCollision)
    opts.onCollision(error.message, {
      into: error.into,
      renameTo: error.renameTo,
    })
  else refused(what, error)
}

/** What the collision prompt calls each kind of row. */
const COLLISION_NOUN = {
  folder: 'Folder',
  document: 'Document',
  repo: 'Repo',
  project: 'Project',
} as const

/** A refusal that a merge or rename can answer (RFC 0357 3i): core found
 *  something at the path, as opposed to the move being malformed. */
function isCollision(message: string): boolean {
  return /already taken|would collide|already a folder there|would both move|already holds|overlaps source|overlaps a nested project|overlaps a source/.test(
    message,
  )
}

/** A move or delete core refused. Said on screen, not only in the console: a
 *  drop that silently does nothing reads as drag and drop being broken. */
function refusedOrCollision(
  what: string,
  error: unknown,
  opts?: MoveOpts,
): void {
  const message = error instanceof Error ? error.message : String(error)
  if (opts?.onCollision && isCollision(message)) opts.onCollision(message)
  else refused(what, error)
}

function refused(what: string, error: unknown): void {
  const message = error instanceof Error ? error.message : String(error)
  console.warn(`[sidebar] ${what} refused`, error)
  window.alert(`${what} refused: ${message}`)
}

/** A catalog entry as read off a project doc: values may be Automerge
 *  strings, so every read goes through String(). */
type DocRefLike = {
  title?: unknown
  path?: unknown
  mime?: unknown
  updatedAt?: unknown
  dataType?: unknown
}

/**
 * Which folders you opened, per project.
 *
 * Per project because the paths are project paths: one key would make a
 * folder you opened in one project open a same-named folder in another.
 * Best-effort like the view key above — a browser refusing storage should cost
 * a remembered preference, never the sidebar.
 */
const toggledKey = (projectId: string) => `mod_w_sidebar_open_v1:${projectId}`
const folderStateKey = (owner: string, path: string) => `${owner}\0${path}`

const loadToggled = (projectId: string): Set<string> => {
  try {
    const raw = localStorage.getItem(toggledKey(projectId))
    const parsed = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? (parsed as string[]) : [])
  } catch {
    return new Set()
  }
}

const saveToggled = (projectId: string, paths: Set<string>): void => {
  try {
    localStorage.setItem(toggledKey(projectId), JSON.stringify([...paths]))
  } catch {
    // best-effort persistence
  }
}

const fileIconFor = (mime: string) =>
  mime === 'text/html' ? FileCode : FileText

export function SidebarPanel({
  projectId: windowProjectId,
  floating = false,
  focused = false,
  openFileId,
  openSessionId,
  openIds = [],
  onOpen,
  onOpenSplit,
  onOpenSession,
  onOpenSessionSplit,
  onCloseOpenItem,
  onCollapse,
  onPin,
  onOpenApps,
  onOpenDiscover,
  spaces = [],
  currentSpaceId = null,
  onSwitchSpace,
  onCreateSpace,
  onRenameSpace,
  onDeleteSpace,
  onReorderSpace,
  spaceActivity,
  listCode,
  listCodeBranches,
}: {
  projectId: string
  floating?: boolean
  focused?: boolean
  openFileId?: string | null
  /** The session showing in the agent panel, so its Home row reads as current
   *  the same way the open file's does. */
  openSessionId?: string | null
  /** The working set, most recent first (useOpenItems). Ids only — resolved here
   * against the catalog, so deleted documents drop out of Home by themselves. */
  openIds?: string[]
  onOpen?: (file: Subject, projectId?: string) => void
  /** The deliberate "beside, not instead": ⌥-click or the row's split button. */
  onOpenSplit?: (file: Subject, projectId?: string) => void
  /** Where a session row leads: the agent panel, not the canvas. */
  onOpenSession?: (id: string) => void
  onOpenSessionSplit?: (id: string) => void
  onCloseOpenItem?: (id: string) => void
  onCollapse?: () => void
  onPin?: () => void
  /**
   * Open the apps modal (RFC 0280 S3).
   *
   * The one piece of apps left in this column, and it is a button rather than a
   * list: this is where people last saw their apps, so it is where they will
   * look for them. The modal itself is the shell's, since it is chrome and
   * outlives a collapsed sidebar.
   */
  onOpenApps?: () => void
  onOpenDiscover?: () => void
  /**
   * The World's spaces (RFC 0281 S5). A strip of rows above the working set:
   * clicking one switches to it, and its chevron shows what it holds.
   *
   * Defaulted so a caller that has no space layer yet renders the column it
   * always did — the sidebar predates spaces and must not require them.
   */
  spaces?: SpaceRef[]
  currentSpaceId?: string | null
  onSwitchSpace?: (spaceId: string) => void
  onCreateSpace?: () => void
  onRenameSpace?: (spaceId: string, name: string) => void
  onDeleteSpace?: (spaceId: string) => void
  onReorderSpace?: (spaceId: string, toIndex: number) => void
  /** What the sessions open in each space are doing, by space id (RFC 0330).
   *  Absent draws no dot. */
  spaceActivity?: ReadonlyMap<string, SpaceActivity>
  /** Where the code tree reads from (RFC 0277 S6). Defaults to the local git
   *  server; a test hands in its own to see what was listed and when. */
  listCode?: ListCodeDir
  listCodeBranches?: ListCodeBranches
}) {
  const worldId = readWorldPointer()
  const [worldDoc] = useDocument((worldId ?? undefined) as AnyDocumentId)
  // The id this window's project lives under now (RFC 0346 3f). A window opened
  // before a move still holds the old id; the repo follows it to the same
  // document, so the project was listed twice, once under each id, and every
  // row in it shared a key with its twin. React then kept stale copies on each
  // render: one document read as seven, and a rename input lost its focus.
  const projectId = resolveProjectMove(worldDoc, windowProjectId)
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  // Every skill an agent here gets (RFC 0283 S4, RFC 0334 S4): the project's,
  // the World's, the installed apps' and Mod's. Not from the project catalog
  // alone: a World skill authored in another project is served to this session
  // and belongs here. Their documents are hidden from the project's files,
  // because the Skills folder is where they are.
  const { skills } = useSkills(projectDoc)
  /** A World that has not loaded yet: records whose app may be its are held
   *  back rather than listed as missing one (RFC 0338 S7). */
  const worldLoading = !!worldId && !worldDoc
  // World skills belong in the home project's tree. Hide old copies still
  // catalogued elsewhere until their migration moves them home. Project skill
  // packages and AGENTS.md are ordinary visible documents (RFC 0357 S9).
  const skillDocIds = useMemo(() => {
    const out = new Set<string>()
    for (const s of projectWorldSkills(worldDoc)) out.add(s.documentId)
    const soul = standingDocIdOf(worldDoc, 'world')
    if (soul) out.add(soul)
    return out
  }, [worldDoc])
  const hiddenIn = useCallback(
    (_doc: unknown): Set<string> => new Set(skillDocIds),
    [skillDocIds],
  )
  /** The World's skills, for the World's Skills folder. */
  const worldSkills = useMemo(
    () =>
      projectWorldSkills(worldDoc).map((s) => ({
        ...s,
        scope: 'world' as const,
      })),
    [worldDoc],
  )
  const { apps, files: documents } = partitionSidebarItems(
    projectDoc,
    hiddenIn(projectDoc),
    worldDoc,
    worldLoading,
  )
  const sessions = useMemo(() => projectSessions(projectDoc), [projectDoc])
  // The focused session's branch, which the files folder follows (RFC 0277
  // S11). A session that never recorded one leaves the folder on this
  // project's last followed worktree.
  const [openSessionDoc] = useDocument(
    (openSessionId ?? undefined) as AnyDocumentId | undefined,
  )
  const sessionCode = projectSessionCode(openSessionDoc)
  const followBranch = sessionCode?.branch ?? null
  const followSourceId =
    (openSessionDoc as { codeSourceId?: string } | undefined)?.codeSourceId ??
    sessionCode?.sourceId ??
    null
  const mirrorRepo = useRepo() as unknown as Repo
  // Session rows show their branch from the registry (S12). A session that
  // recorded its branch before the registry carried one gets it mirrored the
  // first time it is opened here.
  // projectString: a scalar branch (RFC 0357 S11) never `===` the string,
  // which would write the mirror on every render.
  const mirroredBranch = openSessionId
    ? projectString(
        (
          projectDoc as
            | { sessionMeta?: Record<string, { branch?: unknown }> }
            | undefined
        )?.sessionMeta?.[openSessionId]?.branch,
      )
    : undefined
  // `String()` because a branch read off the log can be an ImmutableString,
  // which never `===` the plain string being followed: the guard below would
  // then re-note the same branch on every pass, writing to the project
  // document forever.
  const mirrored =
    mirroredBranch === undefined ? undefined : String(mirroredBranch)
  useEffect(() => {
    if (!openSessionId || !followBranch || !projectDoc) return
    if (mirrored === followBranch) return
    void noteSessionBranch(
      mirrorRepo,
      projectId,
      openSessionId,
      followBranch,
    ).catch(() => {})
  }, [mirrorRepo, projectId, openSessionId, followBranch, mirrored, projectDoc])

  /**
   * THE DIRECTORY IS THE WORLD'S, not this window's project (RFC 0281 S2).
   *
   * The working set above it is still one project's, and that asymmetry is
   * deliberate rather than half-done: the directory answers "what exists", which
   * is a question about the World, while the working set answers "what am I in",
   * which is answered per pane and moves when the panes do. The directory is
   * also the half that has somewhere to put a second project — a collapsible
   * root — where the working set would need every row to say which project it
   * came from.
   *
   * With one project registered this renders exactly what `buildDirectory` did,
   * which `buildWorldDirectory` guarantees and its test pins.
   *
   * THIS WINDOW'S PROJECT IS ALWAYS IN THE LIST, whatever the World says. The
   * World is a synced document and the registry can be empty for entirely
   * ordinary reasons: it has not arrived yet, the pointer is unset, or this
   * project was opened by link and never registered. Trusting it alone empties
   * the directory of the project you are looking at, which is what happened the
   * first time this was written.
   */
  const worldProjects = useWorldProjects()
  const otherProjects = useMemo(
    () => worldProjects.filter((p) => p.projectId !== projectId),
    [worldProjects, projectId],
  )
  const homeProjectId = (worldDoc as { homeProjectId?: string } | undefined)
    ?.homeProjectId
  const projectIds = useMemo(
    () => [
      ...otherProjects.map((p) => p.projectId),
      ...(homeProjectId && homeProjectId !== projectId ? [homeProjectId] : []),
    ],
    [otherProjects, homeProjectId, projectId],
  )
  const projectDocs = useProjectDocs(projectIds)

  /**
   * Make a project (RFC 0281 S2a).
   *
   * The World is a directory of projects and, until now, nothing in the app
   * could put a second one in it: `ProjectRedirect` creates the FIRST on a
   * cold start and there was no second door. So the multi-project half of this
   * RFC was unreachable from the UI that renders it.
   *
   * TWO WRITES, and both matter. `createProject` mints the document and
   * `addProjectToWorld` registers it — a project the World does not list is
   * one the sidebar cannot show, and a registry entry with no document is a root
   * that never fills in. The name is written to both, because the document's
   * `title` and the World entry's `displayName` are two fields and the directory
   * reads the second.
   *
   * DEFAULT NAMED, not prompted: this column renames inline (see the group rows,
   * and `SessionRow`, which says "no modal, no window.confirm"). Renaming a
   * project root is not built yet — and neither is renaming the project you
   * already have, which is called "Project" for the same reason.
   */
  const repo = useRepo() as unknown as Repo
  const boot = useOptionalTabRepoBoot()
  const accountWorld = useSharingAccountWorld()
  const [createFailed, setCreateFailed] = useState(false)
  const onCreateProject = useCallback(() => {
    setCreateFailed(false)
    void (async () => {
      try {
        // `getOrCreateWorld`, not `readWorldPointer`: a browser with no pointer
        // yet should get a World rather than have the button quietly do
        // nothing, which is what a bare read plus an early return did.
        if (boot?.sharing && !accountWorld)
          throw new Error('Open your account before creating a project')
        const { worldId } = boot?.sharing
          ? accountWorld!.location
          : await getOrCreateWorld(repo)
        const name = 'New project'
        const handle = boot?.sharing
          ? (
              await createSharingProject(boot.sharing, accountWorld!.person, {
                title: name,
              })
            ).project
          : createProject(repo, { title: name })
        await addProjectToWorld(repo, worldId, handle.documentId as string, {
          displayName: name,
        })
        if (boot?.sharing) await repo.flush()
      } catch {
        // SAY SO. The first version wrapped this in a bare `void` and swallowed
        // whatever came out, so a failed registration looked identical to a
        // successful one until something else went looking for the project.
        setCreateFailed(true)
      }
    })()
  }, [repo, boot?.sharing, accountWorld])

  /**
   * Rename a project, which the sidebar could not do until now (see the note
   * on `onCreateProject`: every project starts life called "Project").
   *
   * `addProjectToWorld` on a project the World already lists updates that
   * entry in place, so this needs no new mutation. The World's `displayName`
   * is what the directory draws; the project document's own `title` is a
   * different field and stays as it was, the same split `onCreateProject`
   * writes both sides of.
   */
  const onRenameProject = useCallback(
    (id: string, name: string) => {
      void (async () => {
        try {
          const { worldId } = await getOrCreateWorld(repo)
          await addProjectToWorld(repo, worldId, id, { displayName: name })
        } catch {
          // The row keeps the name it had: a rename that did not land must not
          // look like one that did.
        }
      })()
    },
    [repo],
  )

  /**
   * Delete a project from this World's directory.
   *
   * The underlying Automerge document is intentionally left alone: projects
   * may still be open in a pane or reachable by link, while the World is the
   * source of truth for which project roots belong in this sidebar.
   */
  const onDeleteProject = useCallback(
    (id: string) => {
      void (async () => {
        try {
          const { worldId } = await getOrCreateWorld(repo)
          // This window's project when the World does not list it (a test
          // project a session opened, say): there is no entry to remove, and
          // the row is drawn because the window is on it. Deleting it means
          // leaving it, for the home project.
          // Only once the World has arrived: before that every project
          // looks unlisted.
          const listed = (worldDoc as { projects?: object } | undefined)
            ?.projects
          if (
            id === projectId &&
            listed &&
            !worldProjects.some((p) => p.projectId === id)
          ) {
            // The home project, else any project the World lists: never a
            // confirmed delete that silently does nothing (review of PR 364, 5).
            const to =
              homeProjectId && homeProjectId !== id
                ? homeProjectId
                : worldProjects.find((p) => p.projectId !== id)?.projectId
            if (to) window.location.assign(worldPath(worldId, to))
            else
              refused(
                'Delete project',
                new Error('there is no other project to go to'),
              )
            return
          }
          await removeProjectFromWorld(repo, worldId, id)
          try {
            localStorage.removeItem(toggledKey(id))
          } catch {
            // best-effort preference cleanup
          }
        } catch {
          // The row remains in the World-backed list, making a failed delete
          // visibly different from one that landed.
        }
      })()
    },
    [repo, projectId, worldDoc, worldProjects, homeProjectId],
  )

  /**
   * Open the soul file, or a project's instructions, in the editor (RFC 0334
   * 3g). The first open makes the document from the old string; after that
   * the document is the text every agent is served.
   *
   * Opened against the project that catalogues it, read off the document
   * itself: a soul file made from another project lives in that one, and a
   * pane resolved against the wrong catalog draws nothing.
   */
  const onOpenStanding = useCallback(
    (level: 'world' | 'project', owner: string) => {
      void (async () => {
        try {
          const { worldId } = await getOrCreateWorld(repo)
          // Always through ensure, even when the row knows the id: it also
          // carries in a save that reached the old string after the document
          // was made, so the editor shows what agents are served.
          const id = await ensureStandingDocument(repo, level, {
            worldId,
            projectId: owner,
          })
          const handle = await repo.find(id as AnyDocumentId)
          await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
          const catalog = String(
            (handle.doc() as { workspaceId?: unknown } | undefined)
              ?.workspaceId ?? owner,
          )
          onOpen?.(
            {
              id,
              title: STANDING_TITLE[level],
              path: `Skills/${STANDING_TITLE[level]}`,
              mime: 'text/markdown',
            },
            catalog,
          )
        } catch {
          // Nothing opens; the row stays, and a second click tries again.
        }
      })()
    },
    [repo, onOpen],
  )

  // What a project row holds besides documents (RFC 0277 S12): its sessions,
  // newest first, its apps, and whether it has a repository.
  const contentsOf = useCallback(
    (doc: unknown) => ({
      sessions: [...projectSessions(doc)].reverse(),
      folders: Object.entries(
        (doc as { folders?: Record<string, { path: string }> } | undefined)
          ?.folders ?? {},
      ).map(([id, entry]) => ({ id, path: String(entry.path) })),
      apps: partitionSidebarItems(
        doc as never,
        hiddenIn(doc),
        worldDoc,
        worldLoading,
      )
        .apps.filter((a) => !!a.viewDocId)
        .map((a) => ({
          id: a.id,
          name: a.name,
          viewDocId: String(a.viewDocId),
        })),
      // Every project can attach its first source from the Files row.
      hasCode: true,
      sources: projectSources(doc).sources.map((source) => ({
        id: source.id,
        name: source.name,
        ...(source.folder ? { folder: source.folder } : {}),
      })),
    }),
    [hiddenIn, worldDoc, worldLoading],
  )

  const tree = useMemo(() => {
    const mounts = new Map(
      worldProjectMounts(worldDoc).map((mount) => [mount.projectId, mount]),
    )
    const parentOfProject = (id: string) => {
      const mount = mounts.get(id)
      return mount?.parentProjectId
        ? { projectId: mount.parentProjectId, folder: mount.folder }
        : undefined
    }
    const here = worldProjects.find((p) => p.projectId === projectId)
    const countHere = projectTaskCount(projectDoc)
    const projects = [
      // The home project's documents sit at the root, never behind a Home row.
      ...(projectId === homeProjectId
        ? []
        : [
            {
              projectId: projectId,
              // Unlisted by the World, it is named by its own title rather
              // than drawn as its id.
              name:
                here?.displayName ??
                String(
                  (projectDoc as { title?: unknown } | undefined)?.title ?? '',
                ),
              parent: parentOfProject(projectId),
              docs: documents,
              ...contentsOf(projectDoc),
              ...(countHere ? { count: countHere } : {}),
            },
          ]),
      // Another project contributes nothing until its document arrives, which
      // is a root with no children rather than a missing root: the World says
      // it exists, so the row is honest and fills in as it syncs.
      ...otherProjects.map((project) => {
        const doc = projectDocs.get(project.projectId)
        const count = projectTaskCount(doc)
        return {
          projectId: project.projectId,
          name: project.displayName,
          parent: parentOfProject(project.projectId),
          docs: partitionSidebarItems(
            doc,
            hiddenIn(doc),
            worldDoc,
            worldLoading,
          ).files,
          ...contentsOf(doc),
          ...(count ? { count } : {}),
        }
      }),
    ]
    // The World's Skills folder: the soul file, then World skills. Each
    // project's own are in its folder; app and Mod skills are read from the
    // app and from Settings (RFC 0334 3g).
    const soulDocId = standingDocIdOf(worldDoc, 'world')
    const homeDoc =
      homeProjectId === projectId
        ? projectDoc
        : homeProjectId
          ? projectDocs.get(homeProjectId)
          : undefined
    const homeDocs = homeProjectId
      ? partitionSidebarItems(
          homeDoc,
          new Set(
            [...hiddenIn(homeDoc)].filter(
              (id) =>
                id !== soulDocId &&
                !worldSkills.some((skill) => skill.documentId === id),
            ),
          ),
          worldDoc,
          worldLoading,
        ).files
      : []
    return buildWorldDirectory(projects, homeProjectId ? [] : worldSkills, {
      standing: true,
      ...(soulDocId ? { soulDocId } : {}),
      ...(homeProjectId
        ? {
            home: {
              projectId: homeProjectId,
              docs: homeDocs,
              folders: contentsOf(homeDoc).folders,
              sources: projectSources(homeDoc).sources,
            },
          }
        : {}),
    })
  }, [
    worldProjects,
    otherProjects,
    projectDoc,
    projectDocs,
    projectId,
    documents,
    hiddenIn,
    worldSkills,
    worldDoc,
    worldLoading,
    contentsOf,
    homeProjectId,
  ])
  const tasksByProject = useMemo(() => {
    const out = new Map<string, TaskElementTask[]>()
    out.set(projectId, registryTasks(projectDoc))
    for (const project of otherProjects) {
      out.set(
        project.projectId,
        registryTasks(projectDocs.get(project.projectId)),
      )
    }
    return out
  }, [otherProjects, projectDoc, projectDocs, projectId])
  const taskPortsByProject = useMemo(() => {
    const out = new Map<string, TaskPort>()
    for (const id of [projectId, ...otherProjects.map((p) => p.projectId)]) {
      const log = new SessionTaskLog(repo as never, undefined, id)
      out.set(id, {
        get: (taskId) => log.get(taskId),
        update: (taskId, patch) => log.update(taskId, patch),
        // The project registry is already subscribed through useDocument. Its
        // change rerenders this tree and supplies the updated task summaries.
        subscribe: () => () => {},
      })
    }
    return out
  }, [otherProjects, projectId, repo])

  /**
   * A file row must carry its owning project across the click boundary.
   *
   * The directory is World-wide, while a document pane resolves against one
   * project catalog. Passing only Subject lost that catalog identity, so a
   * file under another project was opened against whichever session happened
   * to be focused, resolved to null, and produced an invisible pane.
   */
  const projectByDocumentId = useMemo(() => {
    const out = new Map<string, string>()
    for (const document of documents) out.set(document.id, projectId)
    for (const project of otherProjects) {
      const doc = projectDocs.get(project.projectId)
      const projectDocuments = partitionSidebarItems(
        doc,
        hiddenIn(doc),
        worldDoc,
        worldLoading,
      ).files
      for (const document of projectDocuments) {
        out.set(document.id, project.projectId)
      }
    }
    return out
  }, [
    documents,
    projectId,
    otherProjects,
    projectDocs,
    hiddenIn,
    worldDoc,
    worldLoading,
  ])

  /**
   * Rename a document from its row, the same gesture as a space: double click,
   * type, Enter. Until this there was no way to retitle a document from the
   * window at all, so a document made without a title stayed "Untitled" for
   * good. The title is the catalog's, so it is written to the project that
   * lists the document, not this window's.
   */
  /**
   * A rename is a filesystem rename (RFC 0357 3i): the name in its folder
   * changes, and the title with it. Written in the row's own project; the
   * window's project was a guess that missed every document of another one.
   */
  const renameFile = useCallback(
    (documentId: string, title: string, owner?: string, path?: string) => {
      const project = owner ?? projectByDocumentId.get(documentId) ?? projectId
      void (async () => {
        if (path) {
          const slash = path.lastIndexOf('/')
          const next = `${slash >= 0 ? path.slice(0, slash + 1) : ''}${title}`
          if (next !== path) await moveDocument(repo, project, documentId, next)
        }
        await renameDocument(repo, project, documentId, title)
      })().catch((e) => refused('Rename', e))
    },
    [repo, projectByDocumentId, projectId],
  )

  /**
   * Refile a document or a whole folder (RFC 0338 3c). The tree only offers a
   * drop core would accept, so a refusal here means the catalog changed under
   * the drag; the tree redraws from the catalog either way.
   */
  const moveFile = useCallback(
    (owner: string, documentId: string, path: string, opts?: MoveOpts) => {
      moveDocument(repo, owner, documentId, path).catch((e) =>
        refusedOrCollision('Move', e, opts),
      )
    },
    [repo],
  )
  /** The catalog a project keeps, read from whichever doc this window holds. */
  const catalogOf = useCallback(
    (owner: string): Record<string, DocRefLike> =>
      (
        (owner === projectId ? projectDoc : projectDocs.get(owner)) as
          | { documents?: Record<string, DocRefLike> }
          | undefined
      )?.documents ?? {},
    [projectId, projectDoc, projectDocs],
  )
  /**
   * Every path a project's catalog holds, hidden rows included (RFC 0338).
   * A skill or an app's record is hidden from Documents but still owns its
   * path, and core refuses a move onto it; checking only what the tree shows
   * offered drops core would refuse.
   */
  const occupiedIn = useCallback(
    (owner: string): Set<string> => {
      const out = new Set<string>()
      for (const r of Object.values(catalogOf(owner))) {
        const p = String(r.path ?? '')
        out.add(normalizeDocumentPath(p) ?? p)
      }
      const doc = owner === projectId ? projectDoc : projectDocs.get(owner)
      for (const folder of Object.values(
        (doc as { folders?: Record<string, { path: string }> } | undefined)
          ?.folders ?? {},
      )) {
        const path = String(folder.path ?? '')
        out.add(normalizeDocumentPath(path) ?? path)
      }
      // A folder can exist only because a project or source is mounted there.
      // It still has children to carry on rename, even with no documents or
      // explicit folder entry in this project's catalog.
      for (const mount of worldProjectMounts(worldDoc))
        if (mount.parentProjectId === owner) out.add(mount.folder)
      for (const source of projectSources(doc).sources)
        if (source.folder) out.add(source.folder)
      return out
    },
    [catalogOf, projectId, projectDoc, projectDocs, worldDoc],
  )
  /** The document just deleted, held long enough to undo (RFC 0338). */
  const [lastDeleted, setLastDeleted] = useState<{
    owner: string
    id: string
    ref: DocRefLike
  } | null>(null)
  useEffect(() => {
    if (!lastDeleted) return
    const t = setTimeout(() => setLastDeleted(null), 15000)
    return () => clearTimeout(t)
  }, [lastDeleted])
  const deleteFile = useCallback(
    (owner: string, documentId: string) => {
      const entry = catalogOf(owner)[documentId]
      const ref = entry
        ? {
            title: String(entry.title ?? ''),
            path: String(entry.path ?? ''),
            mime: String(entry.mime ?? ''),
            updatedAt: Number(entry.updatedAt) || Date.now(),
            ...(entry.dataType ? { dataType: String(entry.dataType) } : {}),
          }
        : null
      deleteDocument(repo, owner, documentId).then(
        () => {
          if (ref) setLastDeleted({ owner, id: documentId, ref })
        },
        (e) => refused('Delete', e),
      )
    },
    [repo, catalogOf],
  )
  const undoDelete = useCallback(() => {
    const d = lastDeleted
    if (!d) return
    setLastDeleted(null)
    restoreDocument(repo, d.owner, d.id, {
      title: d.ref.title as string,
      path: d.ref.path as string,
      mime: d.ref.mime as string,
      updatedAt: d.ref.updatedAt as number,
      ...(d.ref.dataType ? { dataType: d.ref.dataType as string } : {}),
    }).catch((e) => refused('Undo', e))
  }, [repo, lastDeleted])
  const moveDocFolder = useCallback(
    (owner: string, from: string, to: string, opts?: MoveOpts) => {
      const hasWorld =
        !!worldId &&
        !!(worldDoc as { homeProjectId?: unknown } | undefined)?.homeProjectId
      // Options only when merging, so a plain move is called as it always was.
      const merge = opts?.merge ? [{ merge: true }] : []
      const moving =
        hasWorld && worldId
          ? moveFolderWithProjects(repo, worldId, owner, from, to, ...merge)
          : moveFolder(repo, owner, from, to, ...merge)
      moving.catch((error) => refusedOrCollision('Move', error, opts))
    },
    [repo, worldId, worldDoc],
  )
  const moveProject = useCallback(
    (id: string, parentId: string, folder: string, opts?: MoveOpts) => {
      if (!worldId) return
      setProjectParent(repo, worldId, id, parentId, folder).catch((e) =>
        refusedOrCollision('Project move', e, opts),
      )
    },
    [repo, worldId],
  )
  const localPersonId = useLocalPersonId()
  /** Projects sitting at a folder inside another, the ones that can become a
   *  folder again. A top-level project has no folder to return to. */
  // Only a project the World places at a folder (RFC 0357 S5). The read gives
  // a top-level project a parent and folder too, so the raw edge decides.
  const nestedProjects = useMemo(
    () =>
      new Set(
        worldProjectMounts(worldDoc)
          .filter((mount) => mount.parent?.folder)
          .map((mount) => mount.projectId),
      ),
    [worldDoc],
  )
  /**
   * Turn a nested project back into a folder (RFC 0357 3c). Membership comes
   * from sharing (RFC 0345), which records no members yet, so the person here
   * is the only one. When 0345 lands its member list replaces this answer;
   * until then core's refusal of a shared project cannot fire from here.
   */
  const dissolveFolderProject = useCallback(
    (id: string) => {
      if (!worldId) return
      const me = localPersonId ?? 'me'
      dissolveProject(repo, worldId, id, async () => ({
        actorId: me,
        memberIds: [me],
      })).catch((error) => refused('Turn into a folder', error))
    },
    [repo, worldId, localPersonId],
  )
  const makeFolderProject = useCallback(
    (owner: string, folder: string) => {
      if (!worldId) return
      makeProject(repo, worldId, owner, folder).catch((error) =>
        window.alert(error instanceof Error ? error.message : String(error)),
      )
    },
    [repo, worldId],
  )
  /** Where a new folder is being named. `header` is the top-level button's;
   *  every other one is drawn under the row it was asked for, because the
   *  header is off screen once the list is scrolled and the form looked like
   *  nothing happened. */
  const [newFolderAt, setNewFolderAt] = useState<{
    owner: string
    parentPath: string
    header?: boolean
  } | null>(null)
  /** The Browse header's New menu. */
  const [newMenuOpen, setNewMenuOpen] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [newFolderError, setNewFolderError] = useState<string | null>(null)
  const newFolder = useCallback(
    (owner: string, parentPath: string, header = false) => {
      setNewFolderAt({ owner, parentPath, ...(header ? { header } : {}) })
      setNewFolderName('')
      setNewFolderError(null)
    },
    [],
  )
  const submitNewFolder = () => {
    if (!newFolderAt) return
    const name = newFolderName.trim()
    if (!name || name.includes('/') || name === '.' || name === '..') {
      setNewFolderError('Choose a folder name without slashes')
      return
    }
    const path = newFolderAt.parentPath
      ? `${newFolderAt.parentPath}/${name}`
      : name
    const creating = worldId
      ? createFolderInWorld(repo, worldId, newFolderAt.owner, path)
      : createFolder(repo, newFolderAt.owner, path)
    creating.then(
      () => setNewFolderAt(null),
      (error) =>
        setNewFolderError(
          error instanceof Error ? error.message : String(error),
        ),
    )
  }
  const newFolderForm = newFolderAt ? (
    <div className="mx-2 mb-1 rounded border border-border p-1.5">
      <div className="truncate text-[11px] text-muted-foreground">
        New folder in {newFolderAt.parentPath || 'top level'}
      </div>
      <div className="mt-1 flex gap-1">
        <input
          aria-label="Folder name"
          value={newFolderName}
          onChange={(event) => setNewFolderName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submitNewFolder()
            if (event.key === 'Escape') setNewFolderAt(null)
          }}
          // biome-ignore lint/a11y/noAutofocus: focus follows an explicit New folder
          autoFocus
          className="min-w-0 flex-1 rounded border border-border bg-background px-1 text-sm"
        />
        <button type="button" onClick={submitNewFolder}>
          Create
        </button>
        <button type="button" onClick={() => setNewFolderAt(null)}>
          Cancel
        </button>
      </div>
      {newFolderError && (
        <div role="alert" className="text-[11px] text-red-500">
          {newFolderError}
        </div>
      )}
    </div>
  ) : null
  const removeFolder = useCallback(
    (owner: string, path: string) => {
      const removing = worldId
        ? removeEmptyFolderInWorld(repo, worldId, owner, path)
        : removeEmptyFolder(repo, owner, path)
      removing.catch((error) =>
        window.alert(error instanceof Error ? error.message : String(error)),
      )
    },
    [repo, worldId],
  )
  const moveSourceAcross = useCallback(
    (
      from: string,
      sourceId: string,
      to: string,
      folder: string,
      opts?: MoveOpts,
    ) => {
      if (!worldId) {
        refused('Repo move', new Error('this window has no World'))
        return
      }
      void (async () => {
        // A placeholder name takes the folder this device has it at.
        const here = await readProjectFolder(from, sourceId)
        const placeholder = projectSources(
          from === projectId ? projectDoc : projectDocs.get(from),
        ).sources.find((s) => s.id === sourceId)?.name
        const name =
          placeholder === 'Project files' && here?.path
            ? here.path.replace(/\/+$/, '').split('/').pop()
            : undefined
        const moved = await moveSourceToProject(
          repo,
          worldId,
          from,
          sourceId,
          to,
          folder,
          name,
        )
        // The daemon rehomes this device's mount a moment later. Ask until it
        // has, so the moved row lists its files instead of "unavailable".
        for (let i = 0; i < 20; i++) {
          if ((await readProjectFolder(to, moved.sourceId))?.path) break
          await new Promise((r) => setTimeout(r, 500))
        }
        window.dispatchEvent(new Event(PROJECT_FOLDER_EVENT))
      })().catch((e) => refusedOrCollision('Repo move', e, opts))
    },
    [repo, worldId, projectId, projectDoc, projectDocs],
  )
  const placeSource = useCallback(
    (owner: string, sourceId: string, folder: string, opts?: MoveOpts) => {
      setProjectSourceFolder(
        repo,
        owner,
        sourceId,
        folder,
        worldId ?? undefined,
      ).catch((error) => refusedOrCollision('Repo move', error, opts))
    },
    [repo, worldId],
  )
  /** A repository is attached where the person invoked the folder action.
   * The device picker supplies the basename; no intermediate Files container
   * or second placement gesture is needed. */
  const attachSource = useCallback(
    (owner: string, parentPath: string) => {
      void (async () => {
        const doc = owner === projectId ? projectDoc : projectDocs.get(owner)
        const explicit = (
          doc as { sources?: Record<string, unknown> } | undefined
        )?.sources
        const legacy = projectSources(doc)
        const mountedDefault = await readProjectFolder(owner, 'default')
        const firstSource =
          !Object.keys(explicit ?? {}).length &&
          !legacy.sources[0]?.vcsDocId &&
          !mountedDefault?.path
        const sourceId = firstSource ? 'default' : crypto.randomUUID()
        const chosen = await chooseSourceFolder(owner, sourceId)
        if (!chosen) return
        try {
          const name = chosen.path.split('/').filter(Boolean).pop() ?? 'Source'
          const folder = folderTarget(name.replace(/\//g, '-'), parentPath)
          const documentPaths = Object.values(
            (
              doc as
                | { documents?: Record<string, { path: string }> }
                | undefined
            )?.documents ?? {},
          ).map((entry) => String(entry.path))
          const projectMounts = worldProjectMounts(worldDoc)
            .filter((mount) => mount.parentProjectId === owner)
            .map((mount) => mount.folder)
          if (
            !canPlaceSourceAt(
              sourceId,
              folder,
              documentPaths,
              legacy.sources,
              projectMounts,
            )
          )
            throw new Error(`${folder} is already occupied`)
          if (chosen.git) {
            if (!chosen.code)
              throw new Error('This Git repository has no commit yet')
            await adoptCode(repo, owner, {
              ...chosen.code,
              sourceId,
              name,
              folder,
              legacyDefaultKind: mountedDefault?.git ? 'git' : 'folder',
            })
          } else {
            await addProjectSource(
              repo,
              owner,
              { id: sourceId, name, kind: 'folder', folder },
              mountedDefault?.git ? 'git' : 'folder',
            )
          }
        } catch (error) {
          await unmountProjectSource(owner, sourceId)
          throw error
        }
      })().catch((error) =>
        window.alert(error instanceof Error ? error.message : String(error)),
      )
    },
    [repo, worldDoc, projectId, projectDoc, projectDocs],
  )
  const sourcesIn = useCallback(
    (owner: string) =>
      projectSources(
        owner === projectId ? projectDoc : projectDocs.get(owner),
      ).sources.map((source) => ({
        id: source.id,
        name: source.name,
        folder: source.folder,
      })),
    [projectId, projectDoc, projectDocs],
  )
  const documentPathsIn = useCallback(
    (owner: string) =>
      Object.values(catalogOf(owner)).map((entry) => String(entry.path)),
    [catalogOf],
  )
  const projectMountsIn = useCallback(
    (owner: string) =>
      worldProjectMounts(worldDoc)
        .filter((mount) => mount.parentProjectId === owner)
        .map((mount) => mount.folder),
    [worldDoc],
  )
  /** What the last move to another project could not do: open an app record
   *  whose app the new project lacks (RFC 0338 3f). Shown, then gone. */
  const [moveNotice, setMoveNotice] = useState<string | null>(null)
  useEffect(() => {
    if (!moveNotice) return
    const t = setTimeout(() => setMoveNotice(null), 10000)
    return () => clearTimeout(t)
  }, [moveNotice])
  const moveAcross = useCallback(
    (
      from: string,
      to: string,
      target: MoveTarget,
      toName: string,
      opts?: MoveOpts,
    ) => {
      moveToProject(repo, from, to, target, {
        worldId,
        ...(opts?.merge ? { merge: true } : {}),
      }).then(
        (res) => {
          if (res.missingApps.length > 0) {
            setMoveNotice(
              `Moved to ${toName}. ${res.missingApps.join(', ')} is not installed there; opening it offers to install it.`,
            )
          }
        },
        (e) => refusedOrCollision('Move to project', e, opts),
      )
    },
    [repo, worldId],
  )
  /**
   * Write a new file into a checkout, against ABSENT so it never replaces one
   * there. A taken name is a collision; with `free` it moves on to `-2`, `-3`
   * until a name is free, which is what rename means here, since the window
   * does not hold the checkout's listing. Returns the path written.
   */
  const saveNewFile = useCallback(
    async (
      path: string,
      content: string,
      project: string,
      sourceId: string,
      branch: string | undefined,
      free: boolean,
    ): Promise<string> => {
      const tried = new Set<string>()
      let at = path
      for (let i = 0; i < 100; i++) {
        const answer = (await fetchVcsFromServer(
          'save',
          [
            {
              path: at,
              content,
              readAt: ABSENT,
              ...(branch ? { branch } : {}),
            },
          ],
          project,
          sourceId,
        )) as { ok: boolean; reason?: string }
        if (answer.ok) return at
        if (answer.reason !== 'changed-on-disk')
          throw new Error(
            `the checkout refused ${at} (${answer.reason ?? 'unknown'})`,
          )
        tried.add(at)
        const next = freeDocumentPath(path, tried)
        if (!free) throw new CheckoutCollision(path, next)
        at = next
      }
      throw new Error(`no free name beside ${path} in the checkout`)
    },
    [],
  )
  /**
   * A document dropped into a checkout (RFC 0357 3i, S16): written there as
   * a file, then gone from the catalog. In that order, so a refused write
   * leaves the document where it was. A file of that name already there asks:
   * rename lands this one at a free name beside it, cancel moves nothing.
   */
  const documentToFile = useCallback(
    (
      fromProject: string,
      documentId: string,
      toProject: string,
      sourceId: string,
      dir: string,
      branch?: string,
      opts?: MoveOpts,
    ) => {
      void (async () => {
        const file = await documentAsFile(repo, fromProject, documentId)
        const path = await saveNewFile(
          dir ? `${dir}/${file.name}` : file.name,
          file.content,
          toProject,
          sourceId,
          branch,
          !!opts?.rename,
        )
        await documentLeftForFile(repo, fromProject, documentId)
        window.dispatchEvent(
          new CustomEvent(CODE_TREE_CHANGED_EVENT, {
            detail: { projectId: toProject, sourceId },
          }),
        )
        setMoveNotice(`${path} is a file in the checkout now, not a document.`)
      })().catch((e) =>
        refusedOrCheckoutCollision('Move into checkout', e, opts),
      )
    },
    [repo, saveNewFile],
  )
  /**
   * A folder of documents dropped into a checkout (3i): every document is
   * read as a file first, so one with no file form refuses the whole folder.
   * Then each is written and leaves the catalog, file on disk before the
   * document goes, and last the empty folder entries. A name taken in the
   * checkout asks before anything is written: merge keeps both (the clashing
   * file lands beside it), rename writes the folder under `opts.as`.
   */
  const folderToFiles = useCallback(
    (
      fromProject: string,
      folder: string,
      toProject: string,
      sourceId: string,
      dir: string,
      branch?: string,
      opts?: MoveOpts & { as?: string },
    ) => {
      void (async () => {
        const { files, folders } = await folderAsFiles(
          repo,
          fromProject,
          folder,
        )
        const base = baseOf(folder)
        const name = opts?.as ?? base
        const root = dir ? `${dir}/${name}` : name
        // The name a rename offers: `drafts-2`, then `drafts-3` if that one
        // is taken too.
        const k = name === base ? 1 : Number(name.slice(base.length + 1)) || 1
        const renameTo = dir ? `${dir}/${base}-${k + 1}` : `${base}-${k + 1}`
        if (!opts?.merge) {
          for (const f of files) {
            const at = `${root}/${f.path}`
            const there = await fetchVcsFromServer(
              'workingFile',
              branch ? [at, branch] : [at],
              toProject,
              sourceId,
            ).catch(() => null)
            if (there !== null && there !== undefined)
              throw new CheckoutCollision(root, renameTo)
          }
        }
        let written = 0
        try {
          for (const f of files) {
            await saveNewFile(
              `${root}/${f.path}`,
              f.content,
              toProject,
              sourceId,
              branch,
              !!opts?.merge,
            )
            await documentLeftForFile(repo, fromProject, f.documentId)
            written++
          }
        } catch (error) {
          // What was written is a file and no longer a document; the rest are
          // still documents. Nothing is in both, nothing is lost: say which.
          const why = error instanceof Error ? error.message : String(error)
          throw new Error(
            written > 0
              ? `${written} of ${files.length} moved; then ${why}`
              : why,
          )
        } finally {
          window.dispatchEvent(
            new CustomEvent(CODE_TREE_CHANGED_EVENT, {
              detail: { projectId: toProject, sourceId },
            }),
          )
        }
        await folderLeftForFiles(repo, fromProject, folders)
        setMoveNotice(
          `${root}/ is files in the checkout now: ${files.length} document${files.length === 1 ? '' : 's'} moved.`,
        )
      })().catch((e) =>
        refusedOrCheckoutCollision('Move into checkout', e, opts),
      )
    },
    [repo, saveNewFile],
  )
  /**
   * A checkout file dragged to another folder of the same checkout (3i): a
   * real move. Written at the new path against ABSENT, then removed from the
   * old one against the hash of what was read. If the old one will not go,
   * the new copy is taken back, so the file is never in both places.
   */
  const moveCheckoutFile = useCallback(
    (
      project: string,
      sourceId: string,
      filePath: string,
      dir: string,
      branch?: string,
      opts?: MoveOpts,
    ) => {
      void (async () => {
        const text = (await fetchVcsFromServer(
          'workingFile',
          branch ? [filePath, branch] : [filePath],
          project,
          sourceId,
        )) as string | null
        if (text === null)
          throw new Error(`${filePath} is binary or missing, so it cannot move`)
        const name = baseOf(filePath)
        const path = await saveNewFile(
          dir ? `${dir}/${name}` : name,
          text,
          project,
          sourceId,
          branch,
          !!opts?.rename,
        )
        const readAt = await contentHash(text)
        const remove = (at: string) =>
          fetchVcsFromServer(
            'remove',
            [{ path: at, readAt, ...(branch ? { branch } : {}) }],
            project,
            sourceId,
          ) as Promise<{ ok: boolean; reason?: string }>
        const answer = await remove(filePath)
        if (!answer.ok) {
          await remove(path)
          throw new Error(
            answer.reason === 'changed-on-disk'
              ? `${filePath} changed on disk while it was being moved`
              : `the checkout refused to remove ${filePath} (${answer.reason ?? 'unknown'})`,
          )
        }
        window.dispatchEvent(
          new CustomEvent(CODE_TREE_CHANGED_EVENT, {
            detail: { projectId: project, sourceId },
          }),
        )
      })().catch((e) => refusedOrCheckoutCollision('Move file', e, opts))
    },
    [saveNewFile],
  )
  /**
   * A checkout file dropped among documents (S16): a document with its text,
   * then the file leaves the disk, against the hash of what was read. A
   * refused removal takes the document back, so the file is never in both.
   */
  const fileToDocument = useCallback(
    (
      fromProject: string,
      sourceId: string,
      filePath: string,
      toProject: string,
      folder: string,
      branch?: string,
    ) => {
      void (async () => {
        const text = (await fetchVcsFromServer(
          'workingFile',
          branch ? [filePath, branch] : [filePath],
          fromProject,
          sourceId,
        )) as string | null
        if (text === null)
          throw new Error(
            `${filePath} is binary or missing, so it cannot become a document`,
          )
        const made = await fileAsDocument(
          repo,
          toProject,
          { path: filePath, content: text },
          folder,
        )
        const answer = (await fetchVcsFromServer(
          'remove',
          [
            {
              path: filePath,
              readAt: await contentHash(text),
              ...(branch ? { branch } : {}),
            },
          ],
          fromProject,
          sourceId,
        )) as { ok: boolean; reason?: string }
        if (!answer.ok) {
          await fileNotTaken(repo, toProject, made.documentId)
          throw new Error(
            answer.reason === 'changed-on-disk'
              ? `${filePath} changed on disk while it was being moved`
              : `the checkout refused to remove ${filePath} (${answer.reason ?? 'unknown'})`,
          )
        }
        window.dispatchEvent(
          new CustomEvent(CODE_TREE_CHANGED_EVENT, {
            detail: { projectId: fromProject, sourceId },
          }),
        )
        setMoveNotice(
          `${filePath} is a document now; git sees it as deleted from the checkout.`,
        )
      })().catch((e) => refused('Move out of checkout', e))
    },
    [repo],
  )

  // Ids resolve against EVERY catalog, so an app row stays an app row and a
  // session row stays a session row. One resolver serves the groups and the
  // ungrouped list, which is what keeps a filed row identical to a loose one.
  //
  // Every project's catalog, not only this window's: a space holds panes from
  // any project, and a window that followed a session into another project
  // listed nothing open. This window's project goes last so it wins a tie.
  const otherCatalogs = useMemo(() => {
    const all = {
      apps: [] as typeof apps,
      documents: [] as typeof documents,
      sessions: [] as typeof sessions,
      /** Which project holds each document and app view: opening one from
       *  Open has to resolve against that project, not this window's. */
      owners: new Map<string, string>(),
    }
    for (const [id, doc] of projectDocs) {
      if (id === projectId || !doc) continue
      const parts = partitionSidebarItems(
        doc,
        hiddenIn(doc),
        worldDoc,
        worldLoading,
      )
      all.apps.push(...parts.apps)
      all.documents.push(...parts.files)
      all.sessions.push(...projectSessions(doc))
      for (const d of parts.files) all.owners.set(d.id, id)
      for (const a of parts.apps)
        if (a.viewDocId) all.owners.set(String(a.viewDocId), id)
    }
    return all
  }, [projectDocs, projectId, hiddenIn, worldDoc, worldLoading])
  const resolve = useCallback(
    (ids: string[]) =>
      resolveHomeItems(ids, {
        apps: [...otherCatalogs.apps, ...apps],
        documents: [...otherCatalogs.documents, ...documents],
        sessions: [...otherCatalogs.sessions, ...sessions],
        web: canEmbedWebPages(),
        owners: otherCatalogs.owners,
      }),
    [otherCatalogs, apps, documents, sessions],
  )

  return (
    <Panel
      floating={floating}
      focused={focused}
      framed={false}
      padded={false}
      header={
        <>
          <div className="flex-1" />
          {onOpenApps && (
            <PanelAction title="Packages (⌘P)" onClick={onOpenApps}>
              <AppWindow size={13} />
            </PanelAction>
          )}
          {onOpenDiscover && (
            <PanelAction title="Discover packages" onClick={onOpenDiscover}>
              <Compass size={13} />
            </PanelAction>
          )}
          {floating ? (
            <PanelAction title="Pin sidebar open" onClick={onPin}>
              <Pin size={13} />
            </PanelAction>
          ) : (
            <PanelAction title="Collapse sidebar (⌘B)" onClick={onCollapse}>
              <PanelLeftClose size={14} />
            </PanelAction>
          )}
        </>
      }
    >
      {/* The one scroller: every section below the header moves together. */}
      <div data-sidebar-scroll className="h-full min-h-0 overflow-y-auto">
        <div className="p-1">
          <SpacesView
            spaces={spaces}
            currentSpaceId={currentSpaceId}
            onCreateSpace={onCreateSpace}
            onRenameSpace={onRenameSpace}
            onDeleteSpace={onDeleteSpace}
            onReorderSpace={onReorderSpace}
            spaceActivity={spaceActivity}
            resolve={resolve}
            openFileId={openFileId}
            openSessionId={openSessionId}
            onSwitchSpace={onSwitchSpace}
            onOpen={onOpen}
            onOpenSession={onOpenSession}
          />
          <WorkingSetView
            openIds={openIds}
            resolve={resolve}
            openFileId={openFileId}
            openSessionId={openSessionId}
            onOpen={onOpen}
            onOpenSplit={onOpenSplit}
            onOpenSession={onOpenSession}
            onOpenSessionSplit={onOpenSessionSplit}
            onClose={onCloseOpenItem}
          />
        </div>
        {/*
          NO TERMINALS SECTION (RFC 0276 S3 walked back).

          This sidebar is the PROJECT: what is open in it, and the files it
          holds. A shell is neither — it is a process on one machine, in no
          catalog, meaning nothing on your other laptop — and a permanent
          section for it made the column a list of two unlike things, with a
          create action for the one thing here that spawns a process.

          Shells are not gone, they moved to where making one belongs: ⌘K's
          "New terminal", and the new-pane page, which is the browser's new-tab
          page for this window and already the place that MAKES things. Both
          reach the same `openTerminal`, so a machine that serves shells still
          offers them, one keystroke away, and a browser tab still offers
          nothing it cannot do.
        */}
        <div className="border-border/60 border-t p-1">
          {/* New project sits on its section's row, as New space does. */}
          <div className="flex items-center">
            <div className="flex-1">
              <SectionLabel>Browse</SectionLabel>
            </div>
            {/* One New menu for the three kinds of folder the tree holds: a
                plain folder, a project, and a repository or folder on disk. */}
            <span className="mr-1">
              <ItemMenu
                label={
                  createFailed
                    ? 'Could not create a project — try again'
                    : 'New'
                }
                open={newMenuOpen}
                onOpenChange={setNewMenuOpen}
                trigger={
                  <Plus
                    size={12}
                    className={createFailed ? 'text-red-500' : ''}
                  />
                }
                items={[
                  {
                    label: 'New folder',
                    onSelect: () =>
                      newFolder(
                        String(
                          (worldDoc as { homeProjectId?: unknown } | undefined)
                            ?.homeProjectId ?? projectId,
                        ),
                        '',
                        true,
                      ),
                  },
                  { label: 'New project', onSelect: onCreateProject },
                  {
                    label: 'Attach repository or folder',
                    onSelect: () =>
                      attachSource(homeProjectId ?? projectId, ''),
                  },
                ]}
              />
            </span>
          </div>
          {newFolderAt?.header ? newFolderForm : null}
          {moveNotice && (
            <div
              role="status"
              className="mx-2 mb-1 rounded-md bg-muted px-2 py-1 text-[12px] text-muted-foreground"
            >
              {moveNotice}
            </div>
          )}
          {lastDeleted && (
            <div
              role="status"
              className="mx-2 mb-1 flex items-center gap-2 rounded-md bg-muted px-2 py-1 text-[12px] text-muted-foreground"
            >
              <span className="min-w-0 flex-1 truncate">
                Deleted {String(lastDeleted.ref.title ?? '')}
              </span>
              <button
                type="button"
                className="rounded px-1 text-foreground hover:bg-background"
                onClick={undoDelete}
              >
                Undo
              </button>
            </div>
          )}
          <FilesView
            projectId={projectId}
            homeProjectId={String(
              (worldDoc as { homeProjectId?: unknown } | undefined)
                ?.homeProjectId ?? '',
            )}
            nodes={tree}
            occupiedIn={occupiedIn}
            tasksByProject={tasksByProject}
            taskPortsByProject={taskPortsByProject}
            openFileId={openFileId}
            onOpen={(file) =>
              onOpen?.(
                file,
                historyProjectOf(file.id) ??
                  branchPaneOf(file.id)?.projectId ??
                  projectByDocumentId.get(file.id) ??
                  projectId,
              )
            }
            onOpenSplit={(file) =>
              onOpenSplit?.(
                file,
                historyProjectOf(file.id) ??
                  branchPaneOf(file.id)?.projectId ??
                  projectByDocumentId.get(file.id) ??
                  projectId,
              )
            }
            onRename={renameFile}
            onMoveDocument={moveFile}
            onMoveFolder={moveDocFolder}
            onMoveProject={moveProject}
            onMakeProject={makeFolderProject}
            onDissolveProject={dissolveFolderProject}
            canDissolveProject={(id) => nestedProjects.has(id)}
            onNewFolder={newFolder}
            {...(newFolderAt && !newFolderAt.header && newFolderForm
              ? {
                  newFolderSlot: {
                    owner: newFolderAt.owner,
                    parentPath: newFolderAt.parentPath,
                    form: newFolderForm,
                  },
                }
              : {})}
            onRemoveFolder={removeFolder}
            onPlaceSource={placeSource}
            onMoveSourceAcross={moveSourceAcross}
            onAttachSource={attachSource}
            sourcesIn={sourcesIn}
            documentPathsIn={documentPathsIn}
            projectMountsIn={projectMountsIn}
            onDeleteDocument={deleteFile}
            onMoveToProject={moveAcross}
            onDocumentToFile={documentToFile}
            onFolderToFiles={folderToFiles}
            onMoveCheckoutFile={moveCheckoutFile}
            onFileToDocument={fileToDocument}
            onRenameProject={onRenameProject}
            onDeleteProject={onDeleteProject}
            onCreateProject={onCreateProject}
            {...(onOpenSession ? { onOpenSession } : {})}
            onOpenApp={(app, owner) => onOpen?.(appAsFile(app as never), owner)}
            onOpenStanding={onOpenStanding}
            appSkill={(appId) => appSkillOf(skills, appId)}
            // Each project's code under its own row (RFC 0277 S12, from RFC
            // 0312 3d): its own git server, its own worktree, following the
            // focused session when that session works in this project (S11).
            renderCode={(owner, depth, folder, sourceId, folderLabel) => (
              <CodeTreeSection
                key={owner}
                projectId={owner}
                sources={projectSources(
                  owner === projectId ? projectDoc : projectDocs.get(owner),
                ).sources.filter((source) =>
                  sourceId ? source.id === sourceId : !source.folder,
                )}
                singleSourceRow={!!sourceId}
                folderLabel={folderLabel}
                defaultSourceId={
                  projectSources(
                    owner === projectId ? projectDoc : projectDocs.get(owner),
                  ).defaultSourceId
                }
                legacyInferredDefault={isAssumedDefaultSource(
                  owner === projectId ? projectDoc : projectDocs.get(owner),
                )}
                onAddSource={() => attachSource(owner, '')}
                onMountSource={(sourceId) => {
                  void chooseSourceFolder(owner, sourceId).catch((error) =>
                    window.alert(
                      error instanceof Error ? error.message : String(error),
                    ),
                  )
                }}
                onRemoveSource={(sourceId) => {
                  void (async () => {
                    await unmountProjectSource(owner, sourceId)
                    await detachProjectSource(repo, owner, sourceId)
                  })().catch((error) =>
                    window.alert(
                      error instanceof Error ? error.message : String(error),
                    ),
                  )
                }}
                onDragSource={folder.dragSource}
                onDragFile={folder.dragFile}
                dropIn={folder.dropIn}
                renderSourceRow={folder.renderSourceRow}
                stateScope={owner}
                embedded
                folderOpen={folder.open}
                onToggleFolder={folder.toggle}
                depth={depth}
                Row={Row}
                openFileId={openFileId}
                onOpen={(file) => onOpen?.(file, owner)}
                onOpenSplit={
                  onOpenSplit ? (file) => onOpenSplit(file, owner) : undefined
                }
                {...(listCode ? { list: listCode } : {})}
                {...(listCodeBranches ? { branches: listCodeBranches } : {})}
                followBranch={followBranch}
                followSourceId={followSourceId}
              />
            )}
            empty={tree.length === 0}
          />
        </div>
      </div>
    </Panel>
  )
}

/**
 * The actions that belong to a PROJECT, on its root row.
 *
 * Shown on hover, like the split control on a file row, and never in the way:
 * the row's own click still collapses the project, which is what it means a
 * hundred times more often than "rename this".
 *
 * Its own overlay closes it — a menu that only
 * closes when you hit the trigger again is a menu you fight.
 */
/**
 * Where the project lives on this Mac, and Change folder (RFC 0337 3i). Drawn
 * only when the desktop daemon answers, so the deployed app in a browser shows
 * nothing here.
 */
function ProjectFolderItem({
  projectId,
  onDone,
}: {
  projectId: string
  onDone: () => void
}) {
  const [folder, setFolder] = useState<ProjectFolder | null>(null)
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'choosing' }
    | { kind: 'refused'; message: string }
    | { kind: 'moved'; path: string }
  >({ kind: 'idle' })
  useEffect(() => {
    let live = true
    void readProjectFolder(projectId).then((f) => {
      if (live) setFolder(f)
    })
    return () => {
      live = false
    }
  }, [projectId])
  if (!folder) return null
  const choose = (force: boolean) => {
    setState({ kind: 'choosing' })
    chooseProjectFolder(projectId, force).then(
      (path) => {
        if (!path) {
          setState({ kind: 'idle' })
          return
        }
        setState({ kind: 'moved', path })
        setTimeout(onDone, 1500)
      },
      (err: Error) => setState({ kind: 'refused', message: err.message }),
    )
  }
  const shown = state.kind === 'moved' ? state.path : folder.path
  return (
    <div className="border-border border-b px-3 pt-1 pb-1.5 text-sm">
      <div className="text-[11px] text-muted-foreground">Folder</div>
      <div
        title={shown ?? undefined}
        className="truncate font-mono text-[11px] text-foreground/80"
      >
        {shown ? shown.replace(/^\/Users\/[^/]+/, '~') : 'None on this Mac'}
        {state.kind !== 'moved' && folder.path && !folder.git
          ? ' · no git'
          : ''}
      </div>
      {state.kind === 'moved' ? (
        <div className="text-[11px] text-muted-foreground">Restarting…</div>
      ) : state.kind === 'choosing' ? (
        <div className="text-[11px] text-muted-foreground">
          Choose in the dialog
        </div>
      ) : (
        <>
          {state.kind === 'refused' ? (
            <div className="text-[11px] text-red-500">{state.message}</div>
          ) : null}
          <button
            type="button"
            className="mt-0.5 text-left text-sm hover:underline"
            onClick={(e) => {
              e.stopPropagation()
              choose(state.kind === 'refused')
            }}
          >
            {state.kind === 'refused' ? 'Change anyway' : 'Change folder…'}
          </button>
        </>
      )}
    </div>
  )
}

function ProjectMenu({
  open,
  onOpenChange,
  onRename,
  onDelete,
  onCreate,
  onNewFolder,
  onAttachSource,
  onDissolve,
  position,
  projectId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Make this nested project an ordinary folder again (RFC 0357 3c). Absent
   *  for a top-level project, which has no folder to become. */
  onDissolve?: () => void
  /** The project, for its folder on this Mac (RFC 0337 3i). */
  projectId?: string
  onRename?: () => void
  onDelete?: () => void
  onCreate?: () => void
  onNewFolder?: () => void
  onAttachSource?: () => void
  /** Pointer position for a desktop context menu; absent means anchor to dots. */
  position?: { x: number; y: number }
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  useEffect(() => {
    if (!open) setConfirmingDelete(false)
  }, [open])

  return (
    <span className="relative">
      <button
        type="button"
        aria-label="Project actions"
        aria-expanded={open}
        title="Project actions"
        className={`rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground ${
          open ? '' : 'opacity-0 group-hover/row:opacity-100'
        }`}
        onClick={(e) => {
          // The row underneath means "collapse this project": a click on the
          // menu must not also do that.
          e.stopPropagation()
          onOpenChange(!open)
        }}
      >
        <MoreHorizontal size={13} />
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-10 cursor-default"
            onClick={(e) => {
              e.stopPropagation()
              onOpenChange(false)
            }}
          />
          <div
            style={
              position
                ? {
                    left: Math.max(
                      8,
                      Math.min(position.x, window.innerWidth - 232),
                    ),
                    top: Math.max(
                      8,
                      Math.min(position.y, window.innerHeight - 220),
                    ),
                  }
                : undefined
            }
            className={`z-20 w-56 overflow-hidden rounded-md border border-border bg-background py-1 text-left shadow-lg ${
              position ? 'fixed' : 'absolute right-0 mt-1'
            }`}
          >
            {projectId ? (
              <ProjectFolderItem
                projectId={projectId}
                onDone={() => onOpenChange(false)}
              />
            ) : null}
            {onRename && (
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenChange(false)
                  onRename()
                }}
              >
                Rename project
              </button>
            )}
            {onCreate && (
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenChange(false)
                  onCreate()
                }}
              >
                New project
              </button>
            )}
            {onNewFolder && (
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenChange(false)
                  onNewFolder()
                }}
              >
                New folder
              </button>
            )}
            {onAttachSource && (
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenChange(false)
                  onAttachSource()
                }}
              >
                Attach repository or folder
              </button>
            )}
            {onDissolve && (
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenChange(false)
                  onDissolve()
                }}
              >
                Turn back into a folder
              </button>
            )}
            {onDelete &&
              (confirmingDelete ? (
                <div className="flex items-center gap-1 px-3 py-1.5 text-sm">
                  <span className="min-w-0 flex-1 text-red-500">
                    Delete project?
                  </span>
                  <button
                    type="button"
                    aria-label="Confirm delete project"
                    title="Confirm delete"
                    className="rounded p-0.5 text-red-500 hover:bg-muted"
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpenChange(false)
                      onDelete()
                    }}
                  >
                    <Check size={13} />
                  </button>
                  <button
                    type="button"
                    aria-label="Cancel delete project"
                    title="Cancel"
                    className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    onClick={(e) => {
                      e.stopPropagation()
                      setConfirmingDelete(false)
                    }}
                  >
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="block w-full px-3 py-1.5 text-left text-sm text-red-500 hover:bg-muted"
                  onClick={(e) => {
                    e.stopPropagation()
                    setConfirmingDelete(true)
                  }}
                >
                  Delete project
                </button>
              ))}
          </div>
        </>
      )}
    </span>
  )
}

/** A skill's document as the canvas opens it. */
function skillAsFile(skill: SidebarSkill): Subject {
  return {
    id: skill.documentId,
    title: skill.title,
    path: `Skills/${skill.title}`,
    mime: 'text/markdown',
  }
}

/**
 * A row's actions behind ⋯, and on right click: the app row's "Show skill"
 * and "Open skill" (RFC 0334 S4). `ProjectMenu`'s shape, without its delete
 * confirmation, which nothing here needs.
 */
function ItemMenu({
  label,
  open,
  onOpenChange,
  position,
  items,
  trigger,
}: {
  label: string
  open: boolean
  onOpenChange: (open: boolean) => void
  position?: { x: number; y: number }
  items: MenuItem[]
  /** The button's icon, shown always. Without it the menu is a row's `...`,
   *  shown on hover. */
  trigger?: ReactNode
}) {
  /** A submenu shown in place of the top list, and an item waiting on its
   *  second click. Both reset when the menu closes. */
  const [sub, setSub] = useState<MenuItem | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)
  useEffect(() => {
    if (!open) {
      setSub(null)
      setConfirming(null)
    }
  }, [open])
  const shown = sub?.children ?? items
  return (
    <span className="relative">
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        title={label}
        className={`rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground ${
          open || trigger ? '' : 'opacity-0 group-hover/row:opacity-100'
        }`}
        onClick={(e) => {
          e.stopPropagation()
          onOpenChange(!open)
        }}
      >
        {trigger ?? <MoreHorizontal size={13} />}
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-10 cursor-default"
            onClick={(e) => {
              e.stopPropagation()
              onOpenChange(false)
            }}
          />
          <div
            style={
              position
                ? {
                    left: Math.max(
                      8,
                      Math.min(position.x, window.innerWidth - 232),
                    ),
                    top: Math.max(
                      8,
                      Math.min(
                        position.y,
                        window.innerHeight - (shown.length * 30 + 48),
                      ),
                    ),
                  }
                : undefined
            }
            className={`z-20 w-44 overflow-hidden rounded-md border border-border bg-background py-1 text-left shadow-lg ${
              position ? 'fixed' : 'absolute right-0 mt-1'
            }`}
          >
            {sub && (
              <button
                type="button"
                className="flex w-full items-center gap-1 px-3 py-1.5 text-left text-muted-foreground text-xs hover:bg-muted"
                onClick={(e) => {
                  e.stopPropagation()
                  setSub(null)
                }}
              >
                <ChevronLeft size={12} /> {sub.label}
              </button>
            )}
            <div className="max-h-72 overflow-y-auto">
              {shown.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className={`flex w-full items-center px-3 py-1.5 text-left text-sm hover:bg-muted ${
                    item.danger ? 'text-red-500' : ''
                  }`}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (item.children) {
                      setSub(item)
                      return
                    }
                    if (item.confirm && confirming !== item.label) {
                      setConfirming(item.label)
                      return
                    }
                    onOpenChange(false)
                    item.onSelect?.()
                  }}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {confirming === item.label ? item.confirm : item.label}
                  </span>
                  {item.children && (
                    <ChevronRight size={12} className="text-muted-foreground" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </span>
  )
}

/**
 * One row of an `ItemMenu`. `children` makes it open a list in place of the
 * menu; `confirm` makes it ask once more, with that text, before it acts,
 * because a sandbox-safe menu has no `window.confirm` to lean on.
 */
interface MenuItem {
  label: string
  onSelect?: () => void
  children?: MenuItem[]
  confirm?: string
  danger?: boolean
}

/** The shared row treatment — every list is made of these. */
function Row({
  icon,
  label,
  title,
  active = false,
  depth = 0,
  trailing,
  onClick,
  onDoubleClick,
  onContextMenu,
  editing,
  onSplit,
  dragId,
  onDragStateChange,
  onDropItem,
  moveDrag,
  moveTarget,
  tone,
}: {
  icon: ReactNode
  label: string
  title?: string
  /** A project's name reads as a heading and its own folders recede, so the
   *  row that owns the rest is the one the eye lands on (RFC 0277 S12). */
  tone?: 'project' | 'folder'
  active?: boolean
  depth?: number
  trailing?: ReactNode
  onClick?: () => void
  /** Double click begins an inline rename, the convention the space rows set. */
  onDoubleClick?: () => void
  /** Desktop context-menu entry point. */
  onContextMenu?: (event: ReactMouseEvent<HTMLDivElement>) => void
  /** Set while this row is being renamed: the label becomes an input, with the
   *  draft and its handlers owned by the list, as `SpacesView` does. */
  editing?: {
    draft: string
    setDraft: (v: string) => void
    commit: () => void
    cancel: () => void
  }
  /** Open this BESIDE what is open, rather than in place of it. Two ways in,
   *  because one of them has to be findable: a button that appears on hover,
   *  and ⌥-click for anyone who has learnt it. */
  onSplit?: () => void
  /** Set on a working-set row: the id this row carries when dragged into a
   *  group (RFC 0280 S2). A Documents-tree row drags with `moveDrag` instead:
   *  filing it moves its path (RFC 0338), which is not what a group does. */
  dragId?: string
  onDragStateChange?: (dragging: boolean) => void
  /** A drop ON this row files the dragged id ABOVE it, which is how ordering
   *  inside a group happens without a second gesture. */
  onDropItem?: (draggedId: string) => void
  /** RFC 0338 3c: a Documents-tree row, draggable to another folder. Called on
   *  drag start and, with false, on drag end, so the list knows what is in
   *  flight: a dragover cannot read the payload, only its type. */
  moveDrag?: (dragging: boolean) => void
  /** RFC 0338 3c: a folder that takes a moved row. `canDrop` decides whether
   *  it lights up, so a drop core would refuse is never offered. */
  moveTarget?: {
    canDrop: () => boolean
    /** Why a drop here is refused; the dev build shows it on the target. */
    refusal?: () => string | null
    onDrop: () => void
  }
}) {
  const [over, setOver] = useState(false)
  const [moveOver, setMoveOver] = useState(false)
  const [refusedHere, setRefusedHere] = useState<string | null>(null)
  const refusalOf = (target: NonNullable<typeof moveTarget>) =>
    target.refusal ? target.refusal() : target.canDrop() ? null : 'refused'
  const isMove = (e: { dataTransfer: DataTransfer }) =>
    e.dataTransfer.types.includes(MOVE_MIME)
  return (
    // A drop target is pointer-only by construction. Filing by keyboard is not
    // built, and RFC 0280 leaves it open.
    // biome-ignore lint/a11y/noStaticElementInteractions: pointer-only drop target
    <div
      className={`group/row relative flex items-center rounded ${
        over ? 'border-foreground/40 border-t' : 'border-transparent border-t'
      } ${moveOver ? 'bg-muted ring-1 ring-foreground/30' : ''} ${
        refusedHere ? 'outline outline-1 outline-red-500/60' : ''
      }`}
      // Dev only: a refused target says why, so testing a drop is not guesswork.
      title={import.meta.env.DEV && refusedHere ? refusedHere : undefined}
      onContextMenu={onContextMenu}
      draggable={dragId !== undefined || moveDrag !== undefined}
      onDragStart={(e) => {
        if (dragId) {
          e.dataTransfer.setData(DRAG_MIME, dragId)
          e.dataTransfer.effectAllowed = 'move'
          onDragStateChange?.(true)
          return
        }
        if (!moveDrag) return
        // A folder inside a folder: the innermost row is the one dragged.
        e.stopPropagation()
        e.dataTransfer.setData(MOVE_MIME, label)
        e.dataTransfer.effectAllowed = 'move'
        moveDrag(true)
      }}
      onDragEnd={() => {
        onDragStateChange?.(false)
        moveDrag?.(false)
      }}
      onDragOver={(e) => {
        if (moveTarget && isMove(e)) {
          const reason = refusalOf(moveTarget)
          if (reason) {
            if (!import.meta.env.DEV) return
            // Dev: take the drop anyway, to say why it is refused.
            e.preventDefault()
            e.dataTransfer.dropEffect = 'none'
            setRefusedHere(reason)
            return
          }
          e.preventDefault()
          e.dataTransfer.dropEffect = 'move'
          setMoveOver(true)
          return
        }
        if (!onDropItem) return
        e.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => {
        setOver(false)
        setMoveOver(false)
        setRefusedHere(null)
      }}
      onDrop={(e) => {
        if (moveTarget && isMove(e)) {
          e.preventDefault()
          setMoveOver(false)
          setRefusedHere(null)
          const reason = refusalOf(moveTarget)
          if (!reason) moveTarget.onDrop()
          else if (import.meta.env.DEV) {
            console.warn(`[sidebar] drop on ${label} refused: ${reason}`)
            window.alert(`Drop on ${label} refused: ${reason}`)
          }
          return
        }
        if (!onDropItem) return
        e.preventDefault()
        setOver(false)
        const dragged = e.dataTransfer.getData(DRAG_MIME)
        if (dragged && dragged !== dragId) onDropItem(dragged)
      }}
    >
      {editing ? (
        <div
          style={{ paddingLeft: 8 + depth * 12 }}
          className="flex h-6 min-w-0 flex-1 items-center gap-2 pr-2 text-[13px]"
        >
          <span className="shrink-0">{icon}</span>
          <input
            value={editing.draft}
            onChange={(e) => editing.setDraft(e.target.value)}
            onBlur={editing.commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') editing.commit()
              if (e.key === 'Escape') editing.cancel()
            }}
            aria-label={`Rename ${label}`}
            // biome-ignore lint/a11y/noAutofocus: focus follows an explicit rename
            autoFocus
            className="min-w-0 flex-1 rounded border border-border bg-background px-1 text-sm outline-none focus:border-blue-500"
          />
        </div>
      ) : (
        <button
          type="button"
          title={[
            title ?? label,
            onSplit ? '⌥ click to open in split' : null,
            onDoubleClick ? 'double click to rename' : null,
          ]
            .filter(Boolean)
            .join(' — ')}
          onClick={(e) => (e.altKey && onSplit ? onSplit() : onClick?.())}
          onDoubleClick={onDoubleClick}
          style={{ paddingLeft: 8 + depth * 12 }}
          className={`flex h-6 min-w-0 flex-1 items-center gap-2 rounded pr-2 text-left text-[13px] ${
            active
              ? 'bg-muted text-foreground'
              : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
          }`}
        >
          <span className="shrink-0">{icon}</span>
          <span
            className={`truncate ${
              // A project reads as a folder like any other (RFC 0357), with
              // no tag: its menu and its tasks say what it is.
              tone === 'project'
                ? ''
                : tone === 'folder'
                  ? 'text-muted-foreground/80'
                  : ''
            }`}
          >
            {label}
          </span>
        </button>
      )}
      {onSplit && (
        <button
          type="button"
          title="Open in split"
          aria-label={`Open ${label} in split`}
          onClick={onSplit}
          className={`absolute rounded p-0.5 text-muted-foreground/0 hover:bg-muted hover:text-foreground group-hover/row:text-muted-foreground ${
            trailing ? 'right-6' : 'right-1'
          }`}
        >
          <Columns2 size={12} />
        </button>
      )}
      {trailing}
    </div>
  )
}

function EmptyNote({ children }: { children: ReactNode }) {
  return (
    <p className="px-2 pt-2 text-[11px] text-muted-foreground/60">{children}</p>
  )
}

/**
 * Which of the two sections you are looking at.
 *
 * The stacked shape needs these in a way the switched one did not: two
 * unlabelled lists with a rule between them read as one list that changed its
 * mind. Quiet on purpose (11px, muted, no caps), because a heading loud enough
 * to compete with the rows costs more than the ambiguity it fixes. S2 hangs
 * group names off the same treatment.
 */
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="px-2 pt-1 pb-0.5 text-[11px] text-muted-foreground/60">
      {children}
    </p>
  )
}

/**
 * The World's spaces (RFC 0281 §7, S5).
 *
 * A row per space: its name, how many panes it holds, and a chevron. Clicking
 * the ROW switches to that space, which is the whole point of the strip —
 * a space is a place you go, not a folder you browse. The chevron opens what it
 * holds without going there, so you can see what is in a space before deciding
 * to leave the one you are in.
 *
 * Within the current space, clicking a child focuses it where it already is;
 * in another space it switches first (RFC 0281 §7). Only the first half is
 * wired here, because focusing a pane in a space you are not in needs the
 * switch to land before the focus can be applied, which is S5's second half.
 *
 * ABOVE the working set rather than replacing it, for now. §7 has spaces
 * absorbing RFC 0280's groups entirely, and that deletion is its own change:
 * it removes `sidebarGroups.ts`, re-keys `useOpenItems` from the project to
 * the space, and drops whatever anyone had filed. Doing it in the same diff as
 * making spaces visible would mean the diff that adds a feature is also the
 * diff that deletes one.
 */
function SpacesView({
  spaces,
  currentSpaceId,
  resolve,
  openFileId,
  openSessionId,
  onSwitchSpace,
  onCreateSpace,
  onRenameSpace,
  onDeleteSpace,
  onReorderSpace,
  spaceActivity,
  onOpen,
  onOpenSession,
}: {
  spaces: SpaceRef[]
  spaceActivity?: ReadonlyMap<string, SpaceActivity>
  currentSpaceId: string | null
  onCreateSpace?: () => void
  onRenameSpace?: (spaceId: string, name: string) => void
  onDeleteSpace?: (spaceId: string) => void
  onReorderSpace?: (spaceId: string, toIndex: number) => void
  resolve: (ids: string[]) => HomeItem[]
  openFileId?: string | null
  openSessionId?: string | null
  onSwitchSpace?: (spaceId: string) => void
  onOpen?: (file: Subject, projectId?: string) => void
  onOpenSession?: (id: string) => void
}) {
  /**
   * Which rows are open, on the DEVICE.
   *
   * `SpaceEntry.collapsed` is a synced field (§7) and this is deliberately not
   * wired to it yet: expanding a row to look at it would otherwise collapse it
   * on someone else's laptop mid-sentence. Whether that field should exist at
   * all is worth asking when the switcher is finished.
   */
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  /**
   * Inline rename and inline delete-confirm, the convention this column keeps:
   * `SessionRow` says it outright — no modal, no window.confirm.
   *
   * SHARED WITH `SpaceTabs`, which draws the same four affordances along the
   * top. Both used to carry their own copy of this state and their own
   * handlers, and one was written by reading the other — which is exactly how
   * two views of one list drift apart.
   */
  const {
    renaming,
    draft,
    confirming,
    setDraft,
    beginRename,
    commitRename,
    cancelRename,
    askDelete,
    confirmDelete,
    cancelDelete,
  } = useSpaceActions({
    ...(onRenameSpace ? { onRename: onRenameSpace } : {}),
    ...(onDeleteSpace ? { onDelete: onDeleteSpace } : {}),
  })
  const reorder = useSpaceReorder({
    spaces,
    axis: 'y',
    ...(onReorderSpace ? { onReorder: onReorderSpace } : {}),
  })

  if (spaces.length === 0) return null

  return (
    <>
      <div className="flex items-center">
        <div className="flex-1">
          <SectionLabel>Spaces</SectionLabel>
        </div>
        {onCreateSpace && (
          <button
            type="button"
            aria-label="New space"
            title="New space"
            className="mr-1 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={() => onCreateSpace()}
          >
            <Plus size={12} />
          </button>
        )}
      </div>
      <div {...reorder.stripProps}>
        {spaces.map((space, index) => {
          const open = expanded.has(space.spaceId)
          const current = space.spaceId === currentSpaceId
          const count = spacePaneCount(space.content)
          return (
            <div key={space.spaceId}>
              <SpaceDropGap on={reorder.isDropGap(index)} />
              <div
                {...reorder.spaceProps(space.spaceId, index)}
                className={`group/space flex items-center gap-1 rounded px-1 py-0.5 text-xs ${
                  current ? 'text-foreground' : 'text-muted-foreground'
                } hover:bg-muted ${
                  reorder.draggingId === space.spaceId ? 'opacity-40' : ''
                }`}
              >
                <button
                  type="button"
                  aria-label={
                    open ? `Collapse ${space.name}` : `Expand ${space.name}`
                  }
                  className="shrink-0 rounded p-0.5 hover:text-foreground"
                  onClick={() =>
                    setExpanded((prev) => {
                      const next = new Set(prev)
                      if (next.has(space.spaceId)) next.delete(space.spaceId)
                      else next.add(space.spaceId)
                      return next
                    })
                  }
                >
                  {open ? (
                    <ChevronDown size={12} />
                  ) : (
                    <ChevronRight size={12} />
                  )}
                </button>
                {renaming === space.spaceId ? (
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={() => commitRename(space.spaceId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitRename(space.spaceId)
                      if (e.key === 'Escape') cancelRename()
                    }}
                    // biome-ignore lint/a11y/noAutofocus: focus follows an explicit rename
                    autoFocus
                    className="min-w-0 flex-1 rounded border border-border bg-background px-1 text-xs outline-none focus:border-blue-500"
                  />
                ) : (
                  <button
                    type="button"
                    className="flex-1 truncate text-left hover:text-foreground"
                    title={`Switch to ${space.name} — double click to rename`}
                    onClick={() => onSwitchSpace?.(space.spaceId)}
                    onDoubleClick={() => {
                      if (!onRenameSpace) return
                      beginRename(space.spaceId, space.name)
                    }}
                  >
                    {space.name || 'Untitled space'}
                  </button>
                )}
                <SpaceDot activity={spaceActivity?.get(space.spaceId)} />
                {confirming === space.spaceId ? (
                  <span className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      title="Confirm delete"
                      className="rounded p-0.5 text-red-400 hover:bg-muted"
                      onClick={() => confirmDelete(space.spaceId)}
                    >
                      <Check size={12} />
                    </button>
                    <button
                      type="button"
                      title="Cancel"
                      className="rounded p-0.5 text-muted-foreground hover:bg-muted"
                      onClick={cancelDelete}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ) : (
                  <>
                    {count > 0 && (
                      <span className="shrink-0 text-[10px] text-muted-foreground">
                        {count}
                      </span>
                    )}
                    {onDeleteSpace && spaces.length > 1 && (
                      <button
                        type="button"
                        aria-label={`Delete ${space.name}`}
                        title="Delete space"
                        className="shrink-0 rounded p-0.5 text-muted-foreground opacity-0 hover:bg-muted hover:text-foreground group-hover/space:opacity-100"
                        onClick={() => askDelete(space.spaceId)}
                      >
                        <X size={12} />
                      </button>
                    )}
                  </>
                )}
              </div>
              {open &&
                resolve(spaceItemIds(space.content)).map((item) => {
                  // `HomeItem` is a union: a session carries its own title, and
                  // everything else carries the Subject that holds one.
                  const label =
                    item.kind === 'session' ? item.title : item.file.title
                  return (
                    <Row
                      key={item.id}
                      label={label}
                      icon={
                        item.kind === 'session' ? (
                          <SessionMark id={item.id} size={13} />
                        ) : (
                          <FileText size={13} />
                        )
                      }
                      active={
                        item.kind === 'session'
                          ? item.id === openSessionId
                          : item.id === openFileId
                      }
                      depth={1}
                      onClick={() => {
                        if (item.kind === 'session') onOpenSession?.(item.id)
                        else onOpen?.(item.file, item.projectId)
                      }}
                    />
                  )
                })}
            </div>
          )
        })}
        <SpaceDropGap on={reorder.isDropGap(spaces.length)} />
      </div>
    </>
  )
}

/**
 * The gap a drop would land in, drawn between two rows.
 *
 * It holds its height whether or not it is lit, so the list does not jump by a
 * row's worth of pixels as the indicator moves between gaps — which would move
 * the very row you are aiming at.
 */
function SpaceDropGap({ on }: { on: boolean }) {
  return (
    <div
      aria-hidden
      className={`mx-1 h-0.5 rounded-full ${on ? 'bg-blue-500' : 'bg-transparent'}`}
    />
  )
}

/**
 * The working set: what you have opened in this space, most recent first.
 *
 * RFC 0280's GROUPS ARE GONE (RFC 0281 §7). A group was a hand made list of ids
 * beside this one, and a space is a named arrangement you can switch to that
 * already holds ids — keeping both meant filing something and opening it were
 * separate acts with separate homes. Groups existed to defeat the recency cap
 * below; a space has no cap and no recency order, so its tree holds what you
 * put there until you take it out, which is everything filing was for.
 *
 * What is left here is the tail: things you touched in this space that are no
 * longer on screen. That is a convenience, not a record, which is why it stays
 * capped and on the device.
 */
function WorkingSetView({
  openIds,
  resolve,
  openFileId,
  openSessionId,
  onOpen,
  onOpenSplit,
  onOpenSession,
  onOpenSessionSplit,
  onClose,
}: {
  openIds: string[]
  resolve: (ids: string[]) => HomeItem[]
  openFileId?: string | null
  openSessionId?: string | null
  onOpen?: (file: Subject, projectId?: string) => void
  onOpenSplit?: (file: Subject) => void
  onOpenSession?: (id: string) => void
  onOpenSessionSplit?: (id: string) => void
  onClose?: (id: string) => void
}) {
  const items = resolve(openIds)

  return (
    <>
      <SectionLabel>Open</SectionLabel>
      {items.map((item) => (
        <WorkingSetRow
          key={item.id}
          item={item}
          active={
            item.kind === 'session'
              ? item.id === openSessionId
              : item.id === openFileId
          }
          onOpen={onOpen}
          onOpenSplit={onOpenSplit}
          onOpenSession={onOpenSession}
          onOpenSessionSplit={onOpenSessionSplit}
          onClose={onClose}
        />
      ))}

      {items.length === 0 && (
        <EmptyNote>
          Nothing open. Pick something from Files, open a session, or press ⌘T
          for a page on the web.
        </EmptyNote>
      )}
    </>
  )
}

/** One row of the working set. Icon by KIND, not by mime: a page and an html
 *  document are the same mime and belong to different places, which is exactly
 *  what the glyph is there to say. */
function WorkingSetRow({
  item,
  active,
  onOpen,
  onOpenSplit,
  onOpenSession,
  onOpenSessionSplit,
  onClose,
}: {
  item: HomeItem
  active: boolean
  onOpen?: (file: Subject, projectId?: string) => void
  onOpenSplit?: (file: Subject) => void
  onOpenSession?: (id: string) => void
  onOpenSessionSplit?: (id: string) => void
  onClose?: (id: string) => void
}) {
  const label = item.kind === 'session' ? item.title : item.file.title
  const hover = item.kind === 'session' ? item.title : item.file.path
  // A session draws its own mark (RFC 0311), so two sessions in the list are
  // told apart before their titles are read; everything else draws its kind.
  const Icon =
    item.kind === 'web'
      ? Globe
      : item.kind === 'app'
        ? AppWindow
        : item.kind === 'session'
          ? null
          : fileIconFor(item.file.mime)
  // An app, or a record of one, draws the app's tile (RFC 0332).
  const tileApp =
    item.kind === 'app' || item.kind === 'file' ? item.file.appId : undefined

  return (
    <Row
      icon={
        Icon ? (
          <AppIcon
            appId={tileApp}
            mime={item.kind === 'file' ? item.file.mime : undefined}
            size={12}
            fallback={<Icon size={13} />}
          />
        ) : (
          <SessionMark id={item.id} size={13} />
        )
      }
      label={label}
      title={hover}
      active={active}
      onClick={() =>
        item.kind === 'session'
          ? onOpenSession?.(item.id)
          : onOpen?.(item.file, item.projectId)
      }
      onSplit={() =>
        item.kind === 'session'
          ? onOpenSessionSplit?.(item.id)
          : onOpenSplit?.(item.file)
      }
      trailing={
        onClose ? (
          <button
            type="button"
            title="Remove from open"
            aria-label={`Remove ${label} from open`}
            onClick={() => onClose(item.id)}
            className="absolute right-1 rounded p-0.5 text-muted-foreground/0 hover:bg-muted hover:text-foreground group-hover/row:text-muted-foreground"
          >
            <X size={12} />
          </button>
        ) : undefined
      }
    />
  )
}

function FilesView({
  projectId,
  homeProjectId,
  nodes,
  tasksByProject,
  taskPortsByProject,
  openFileId,
  onOpen,
  onOpenSplit,
  onRename,
  onMoveDocument,
  onMoveFolder,
  onMoveProject,
  onMakeProject,
  onDissolveProject,
  canDissolveProject,
  onNewFolder,
  newFolderSlot,
  onRemoveFolder,
  onPlaceSource,
  onMoveSourceAcross,
  onAttachSource,
  sourcesIn,
  documentPathsIn,
  projectMountsIn,
  onDeleteDocument,
  onMoveToProject,
  onDocumentToFile,
  onFolderToFiles,
  onMoveCheckoutFile,
  onFileToDocument,
  occupiedIn,
  onRenameProject,
  onDeleteProject,
  onCreateProject,
  empty,
  onOpenSession,
  onOpenApp,
  onOpenStanding,
  appSkill,
  renderCode,
}: {
  projectId: string
  homeProjectId: string
  nodes: TreeNode[]
  tasksByProject: ReadonlyMap<string, TaskElementTask[]>
  taskPortsByProject: ReadonlyMap<string, TaskPort>
  openFileId?: string | null
  onOpen?: (file: Subject, projectId?: string) => void
  onOpenSplit?: (file: Subject) => void
  /** Retitle a document. A folder renames through `onMoveFolder`: it is a
   *  path prefix, so its name is written to every document under it. */
  onRename?: (
    documentId: string,
    title: string,
    projectId?: string,
    path?: string,
  ) => void
  /** RFC 0338: give a document in `projectId` a new path. */
  onMoveDocument?: (
    projectId: string,
    documentId: string,
    path: string,
    opts?: MoveOpts,
  ) => void
  /** RFC 0338: move every document under `from/` to `to/`. */
  onMoveFolder?: (
    projectId: string,
    from: string,
    to: string,
    opts?: MoveOpts,
  ) => void
  onMoveProject?: (
    projectId: string,
    parentProjectId: string,
    folder: string,
    opts?: MoveOpts,
  ) => void
  onMakeProject?: (projectId: string, folder: string) => void
  /** Turn a nested project back into a folder (RFC 0357 3c). */
  onDissolveProject?: (projectId: string) => void
  canDissolveProject?: (projectId: string) => boolean
  onNewFolder?: (projectId: string, parentPath: string) => void
  /** The New folder form, drawn under the row it was asked for. */
  newFolderSlot?: { owner: string; parentPath: string; form: ReactNode }
  onRemoveFolder?: (projectId: string, path: string) => void
  onPlaceSource?: (
    projectId: string,
    sourceId: string,
    folder: string,
    opts?: MoveOpts,
  ) => void
  /** RFC 0357 S14: a repo dropped on another project's folder. */
  onMoveSourceAcross?: (
    fromProjectId: string,
    sourceId: string,
    toProjectId: string,
    folder: string,
    opts?: MoveOpts,
  ) => void
  onAttachSource?: (projectId: string, parentPath: string) => void
  sourcesIn?: (
    projectId: string,
  ) => { id: string; name: string; folder?: string }[]
  documentPathsIn?: (projectId: string) => string[]
  projectMountsIn?: (projectId: string) => string[]
  /** Unlist a document from `projectId`'s catalog, as delete_document does. */
  onDeleteDocument?: (projectId: string, documentId: string) => void
  /** RFC 0338 S5: move a document or folder from one project to another.
   *  `toName` is the project's name, for what the move says afterwards. */
  onMoveToProject?: (
    from: string,
    to: string,
    target: MoveTarget,
    toName: string,
    opts?: MoveOpts,
  ) => void
  /** RFC 0357 S16: a document dropped into a checkout folder (`dir` '' is
   *  the source's root; `branch` names a sibling worktree's disk). */
  onDocumentToFile?: (
    fromProjectId: string,
    documentId: string,
    toProjectId: string,
    sourceId: string,
    dir: string,
    branch?: string,
    opts?: MoveOpts,
  ) => void
  /** RFC 0357 3i: a folder of documents dropped into a checkout folder;
   *  `opts.as` names the folder there when renamed. */
  onFolderToFiles?: (
    fromProjectId: string,
    folder: string,
    toProjectId: string,
    sourceId: string,
    dir: string,
    branch?: string,
    opts?: MoveOpts & { as?: string },
  ) => void
  /** RFC 0357 3i: a checkout file dragged to another folder of the same
   *  checkout. */
  onMoveCheckoutFile?: (
    projectId: string,
    sourceId: string,
    filePath: string,
    dir: string,
    branch?: string,
    opts?: MoveOpts,
  ) => void
  /** RFC 0357 S16: a checkout file dropped on a folder of documents. */
  onFileToDocument?: (
    fromProjectId: string,
    sourceId: string,
    filePath: string,
    toProjectId: string,
    folder: string,
    branch?: string,
  ) => void
  /** Every path `projectId`'s catalog holds, hidden rows included: what a
   *  move is checked against, so the tree never offers one core refuses. */
  occupiedIn?: (projectId: string) => Set<string>
  /** A PROJECT root renames too, but into the World's registry rather than the
   *  document catalog, so it is a second handler rather than a flag. */
  onRenameProject?: (projectId: string, name: string) => void
  /** Remove a project root from the World's directory. */
  onDeleteProject?: (projectId: string) => void
  onCreateProject?: () => void
  /** RFC 0277 S12: a session row in a project's Sessions folder. */
  onOpenSession?: (sessionId: string) => void
  /** An app row in a project's Apps folder, opened against that project. */
  onOpenApp?: (
    app: { id: string; name: string; viewDocId: string },
    projectId: string,
  ) => void
  /** The soul file or a project's instructions, opened in the editor (RFC
   *  0334 3g). `owner` is the project the row sits in. */
  onOpenStanding?: (level: 'world' | 'project', owner: string) => void
  /** The skill an app row's menu opens (RFC 0334 3g). */
  appSkill?: (appId: string) => SidebarSkill | undefined
  /** Draws a project's Code folder contents: the git tree, lazily. */
  renderCode?: (
    projectId: string,
    depth: number,
    folder: {
      open: boolean
      toggle: () => void
      dragSource?: (sourceId: string, dragging: boolean) => void
      dragFile?: CodeTreeSectionProps['onDragFile']
      dropIn?: CodeTreeSectionProps['dropIn']
      renderSourceRow?: CodeTreeSectionProps['renderSourceRow']
    },
    sourceId?: string,
    folderLabel?: string,
  ) => ReactNode
  empty: boolean
}) {
  /**
   * The row being renamed and its draft, one at a time. The draft is local
   * so a keystroke does not sync; the commit is one catalog write. An empty or
   * unchanged draft is a cancel, not a write.
   */
  const [renaming, setRenaming] = useState<{
    id: string
    draft: string
    /** The row's own project and path: the rename is written there. */
    project: string
    path: string
  } | null>(null)
  const commitRename = (current: string) => {
    if (!renaming) return
    const next = renaming.draft.trim()
    setRenaming(null)
    if (next && next !== current)
      onRename?.(renaming.id, next, renaming.project, renaming.path)
  }
  /**
   * The project row's menu, and its rename.
   *
   * Kept apart from the file rename above because they write to different
   * places: a file's name is the document catalog, a project's is the World's
   * registry. One state holding both would be one commit handler asking which
   * kind it had, every time.
   */
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const [contextMenu, setContextMenu] = useState<{
    id: string
    x: number
    y: number
  } | null>(null)
  const [renamingProject, setRenamingProject] = useState<{
    id: string
    draft: string
  } | null>(null)
  const commitProjectRename = (current: string) => {
    if (!renamingProject) return
    const next = renamingProject.draft.trim()
    setRenamingProject(null)
    if (next && next !== current) onRenameProject?.(renamingProject.id, next)
  }
  /**
   * RFC 0338: the folder being renamed, and what is being dragged. A dragover
   * can read a payload's type but not its contents, so the row in flight is
   * held here for the folders deciding whether to light up.
   */
  const [renamingFolder, setRenamingFolder] = useState<{
    project: string
    path: string
    draft: string
  } | null>(null)
  const moving = useRef<{
    project: string
    /** `sourceFile` is a file inside a checkout (RFC 0357 S16): `path` is
     *  then repo-relative, with the source and the ref the tree listed. */
    kind: 'file' | 'folder' | 'project' | 'source' | 'sourceFile'
    path: string
    id?: string
    name?: string
    sourceId?: string
    ref?: string | null
  } | null>(null)
  const [draggingProject, setDraggingProject] = useState(false)
  /** A drop that landed where something already is (RFC 0357 3i). A folder
   *  chooses merge, rename or cancel; a document, repo or project, which has
   *  nothing to merge into, chooses rename or cancel. */
  const [collision, setCollision] = useState<{
    kind: 'folder' | 'document' | 'repo' | 'project'
    message: string
    into: string
    /** Where rename lands it: the free name beside what is there. */
    renameTo: string
    /** The clash is between documents inside the moved folder, not with
     *  anything at the destination: renaming the folder cannot fix it. */
    within?: boolean
    merge?: () => void
    rename: () => void
  } | null>(null)
  /**
   * FOLDERS START CLOSED, and stay however you left them.
   *
   * This used to expand every folder at every depth, to fix a real bug: a
   * sidebar that opens with everything shut looks like an empty project. That
   * held while the tree was shallow and hand-made.
   *
   * RFC 0274 ended it. A pushed app contributes its whole source tree, one
   * document per file, so `apps/Review canvas/src/vendor/...` arrives in full
   * and expanding it buries the project's own documents under two apps' worth
   * of build scaffolding. The original bug and its fix swapped places.
   *
   * The empty-project worry is answered by the folder ROWS, not their
   * contents: `apps`, `plans` and `marketing` sitting there closed says the
   * project has things in it perfectly well. Opening them is a choice, and it
   * is now remembered, because a default you have to undo on every reload is
   * the same annoyance wearing a different hat.
   */
  // Paths belong to projects. Two projects can both have `plans` or an
  // unplaced `:source/default`, but opening one must not open the other.
  const allPaths = useMemo(() => {
    const collect = (list: TreeNode[], owner: string): string[] =>
      list.flatMap((node) => {
        if (node.kind !== 'folder') return []
        const nextOwner = node.root ? node.path : owner
        return [
          folderStateKey(nextOwner, node.path),
          ...collect(node.children, nextOwner),
        ]
      })
    return collect(nodes, homeProjectId || projectId)
  }, [nodes, homeProjectId, projectId])
  const [toggled, setToggled] = useState<Set<string>>(() =>
    loadToggled(projectId),
  )
  const toggle = useCallback(
    (owner: string, path: string) => {
      setToggled((prev) => {
        const next = new Set(prev)
        const key = folderStateKey(owner, path)
        if (!next.delete(key)) next.add(key)
        saveToggled(projectId, next)
        return next
      })
    },
    [projectId],
  )
  // A tree that loses a folder must not keep it in `toggled` forever, or a
  // folder deleted and later re-created comes back in its non-default state for
  // no visible reason.
  useEffect(() => {
    setToggled((prev) => {
      if (prev.size === 0) return prev
      const live = new Set(allPaths)
      const kept = [...prev].filter((p) => live.has(p))
      return kept.length === prev.size ? prev : new Set(kept)
    })
  }, [allPaths])

  /** Every document path in each project's Documents folder, which is what a
   *  drop is checked against (RFC 0338 3c). */
  const docPaths = useMemo(() => {
    const out = new Map<string, Set<string>>()
    const collect = (list: TreeNode[], into: Set<string>) => {
      for (const n of list) {
        // As the tree reads it, which tolerates stray slashes, so a drop is
        // checked against the same path core will compare.
        if (n.kind === 'file')
          into.add(n.doc.path.split('/').filter(Boolean).join('/'))
        else if (n.kind === 'folder' && !n.root) {
          if (n.explicitId) into.add(n.path)
          collect(n.children, into)
        }
      }
    }
    const walk = (list: TreeNode[]) => {
      for (const n of list) {
        if (n.kind !== 'folder') continue
        if (n.root) {
          const paths = new Set<string>()
          collect(n.children, paths)
          out.set(n.path, paths)
          walk(n.children)
        } else walk(n.children)
      }
    }
    walk(nodes)
    if (homeProjectId) {
      const paths = new Set<string>()
      collect(nodes, paths)
      out.set(homeProjectId, paths)
    }
    return out
  }, [nodes, homeProjectId])
  const pathsIn = (project: string) => docPaths.get(project) ?? new Set()
  /** What a move is checked against: the whole catalog when the host gives
   *  it, the visible tree otherwise. `pathsIn` stays the list of folders. */
  const takenIn = (project: string) => occupiedIn?.(project) ?? pathsIn(project)
  /** What stands in the way of source `sourceId` at `target` in `project`. */
  const sourceClashAt = (project: string, sourceId: string, target: string) =>
    sourcePlacementClash(
      sourceId,
      target,
      documentPathsIn?.(project) ?? [],
      sourcesIn?.(project) ?? [],
      projectMountsIn?.(project) ?? [],
    )
  /** `to`, or the free name beside it, for a folder, repo or project. */
  const freeBeside = (project: string, to: string) =>
    freeFolderTarget(to, new Set([...takenIn(project), to]))

  /** A move carries the folder's open state with it, so a renamed folder does
   *  not snap shut as if it were new. */
  const carryOpen = (owner: string, from: string, to: string) =>
    setToggled((prev) => {
      const next = carryFolderKeys(
        prev,
        folderStateKey(owner, from),
        folderStateKey(owner, to),
      )
      saveToggled(projectId, next)
      return next
    })

  /** What dropping the row in flight on `folder` of `project` would do. */
  /** The projects in the tree, by id, with the name each root row shows. */
  const projectNames = new Map<string, string>()
  const namesIn = (list: TreeNode[]) => {
    for (const node of list)
      if (node.kind === 'folder') {
        if (node.root) projectNames.set(node.path, node.name)
        namesIn(node.children)
      }
  }
  namesIn(nodes)
  /** Why dropping the row in flight on `folder` of `project` is refused, or
   *  null when it lands. The dev build shows the reason on the target. */
  const dropRefusal = (project: string, folder: string): string | null => {
    const m = moving.current
    if (!m) return 'nothing is being dragged'
    if (m.kind === 'sourceFile') {
      // RFC 0357 S16: a checkout file lands among documents, under a free
      // name as a document does. Only from a disk the daemon can change.
      if (!onFileToDocument) return 'files cannot become documents here'
      if (m.ref && !m.ref.startsWith('~'))
        return 'this lists a commit, not a working tree; pick the working tree first'
      return null
    }
    if (m.kind === 'project') {
      if (!onMoveProject || !m.id) return 'projects cannot move here'
      return m.id === project ? 'a project cannot go inside itself' : null
    }
    if (m.kind === 'source') {
      if (!onPlaceSource || !m.id || !m.name) return 'repos cannot move here'
      const target = folderTarget(m.name, folder)
      // Into another project (S14): core checks the landing there.
      if (m.project !== project)
        return onMoveSourceAcross
          ? null
          : 'repos cannot move to another project here'
      if (target === m.path) return 'the repo is already here'
      // Something AT the target asks (rename lands beside it); a target
      // INSIDE a document, repo or project is refused, as no rename fixes it.
      return sourceClashAt(project, m.id, target) === 'inside'
        ? `${target} is inside a document, another repo or a nested project`
        : null
    }
    if (m.project !== project) {
      // RFC 0338 S5: into another project. A document always lands (under a
      // free name); a folder only when nothing it carries is taken there.
      if (!onMoveToProject) return 'cannot move to another project here'
      // A folder lands even where something is: a clash asks (RFC 0357 3i).
      return null
    }
    if (m.kind === 'file')
      return canDropDocument(m.path, folder)
        ? null
        : 'the document is already in this folder'
    const to = folderTarget(m.path, folder)
    if (to === m.path) return 'the folder is already here'
    if (to.startsWith(`${m.path}/`)) return 'a folder cannot go inside itself'
    const carried = [...takenIn(project)].some(
      (p) => p === m.path || p.startsWith(`${m.path}/`),
    )
    if (!carried) return `${m.path} holds nothing this project knows about`
    // A clash with what is already at `to` asks rather than refuses.
    return null
  }
  const canDropOn = (project: string, folder: string): boolean =>
    dropRefusal(project, folder) === null
  /**
   * Move a document to `folder` of its own project. A name taken there asks
   * (RFC 0357 3i): rename lands it beside the holder as `notes-2.md`. The
   * window knows the catalog, so a visible clash asks before any write; one
   * core alone can see asks when core refuses.
   */
  const moveDocumentTo = (
    project: string,
    id: string,
    docPath: string,
    folder: string,
  ) => {
    const to = documentTarget(docPath, folder)
    const renameTo = freeDocumentTarget(docPath, folder, takenIn(project))
    const ask = (message: string) =>
      setCollision({
        kind: 'document',
        message,
        into: to,
        renameTo,
        rename: () => onMoveDocument?.(project, id, renameTo),
      })
    if (renameTo !== to) ask(`${to} is already taken`)
    else onMoveDocument?.(project, id, to, { onCollision: ask })
  }
  /** The same, into `folder` of another project (RFC 0338 S5). */
  const moveDocumentAcross = (
    from: string,
    project: string,
    id: string,
    docPath: string,
    folder: string,
  ) => {
    const name = projectNames.get(project) ?? 'the project'
    const to = documentTarget(docPath, folder)
    const renameTo = freeDocumentTarget(docPath, folder, takenIn(project))
    const send = (path: string, opts?: MoveOpts) =>
      onMoveToProject?.(from, project, { documentId: id, path }, name, opts)
    const ask = (message: string) =>
      setCollision({
        kind: 'document',
        message,
        into: to,
        renameTo,
        rename: () => send(renameTo),
      })
    if (renameTo !== to) ask(`${to} is already taken there`)
    else send(to, { onCollision: ask })
  }
  const dropOn = (project: string, folder: string) => {
    const m = moving.current
    moving.current = null
    if (!m) return
    if (m.kind === 'project' && m.id) {
      const id = m.id
      const to = folderTarget(m.path, folder)
      const renameTo = freeBeside(project, to)
      // Core sees the whole World; a folder already holding a project asks.
      onMoveProject?.(id, project, to, {
        onCollision: (message) =>
          setCollision({
            kind: 'project',
            message,
            into: to,
            renameTo,
            rename: () => onMoveProject?.(id, project, renameTo),
          }),
      })
      return
    }
    if (m.kind === 'sourceFile' && m.sourceId) {
      onFileToDocument?.(
        m.project,
        m.sourceId,
        m.path,
        project,
        folder,
        m.ref?.startsWith('~') ? m.ref.slice(1) : undefined,
      )
      return
    }
    if (m.kind === 'source' && m.id && m.name) {
      const id = m.id
      const target = folderTarget(m.name, folder)
      const renameTo = freeBeside(project, target)
      if (m.project !== project) {
        const from = m.project
        onMoveSourceAcross?.(from, id, project, target, {
          onCollision: (message) =>
            setCollision({
              kind: 'repo',
              message,
              into: target,
              renameTo,
              rename: () => onMoveSourceAcross?.(from, id, project, renameTo),
            }),
        })
        return
      }
      const ask = (message: string) =>
        setCollision({
          kind: 'repo',
          message,
          into: target,
          renameTo,
          rename: () => onPlaceSource?.(project, id, renameTo),
        })
      if (sourceClashAt(project, id, target) === 'taken')
        ask(`${target} is already taken`)
      else onPlaceSource?.(project, id, target, { onCollision: ask })
      return
    }
    if (m.project !== project) {
      const name = projectNames.get(project) ?? 'the project'
      if (m.kind === 'file' && m.id) {
        moveDocumentAcross(m.project, project, m.id, m.path, folder)
      } else if (m.kind === 'folder') {
        const to = folderTarget(m.path, folder)
        const renameTo = freeBeside(project, to)
        const across = (at: string, merge?: boolean) =>
          onMoveToProject?.(
            m.project,
            project,
            { folder: m.path, to: at },
            name,
            {
              ...(merge ? { merge: true } : {}),
              onCollision: (message) =>
                setCollision({
                  kind: 'folder',
                  message,
                  into: to,
                  renameTo,
                  within: /would both move/.test(message),
                  merge: () => across(to, true),
                  rename: () => across(renameTo),
                }),
            },
          )
        across(to)
      }
      return
    }
    if (m.kind === 'file' && m.id) {
      moveDocumentTo(project, m.id, m.path, folder)
    } else if (m.kind === 'folder') {
      const to = folderTarget(m.path, folder)
      const renameTo = freeBeside(project, to)
      const within = (at: string, merge?: boolean) => {
        carryOpen(project, m.path, at)
        onMoveFolder?.(project, m.path, at, {
          ...(merge ? { merge: true } : {}),
          onCollision: (message) =>
            setCollision({
              kind: 'folder',
              message,
              into: to,
              renameTo,
              within: /would both move/.test(message),
              merge: () => within(to, true),
              rename: () => within(renameTo),
            }),
        })
      }
      within(to)
    }
  }
  /**
   * Why dropping the row in flight on a folder INSIDE a checkout is refused,
   * or null (RFC 0357 S16). A document lands as a file; anything else says
   * why not, so a drop never silently does nothing.
   */
  const sourceDropRefusal = (
    project: string,
    target: CodeDropTarget,
  ): string | null => {
    const m = moving.current
    if (!m) return 'nothing is being dragged'
    if (!target.mounted) return 'this device has no checkout of this repo'
    if (target.ref && !target.ref.startsWith('~'))
      return 'this lists a commit, not a working tree; pick the working tree first'
    if (m.kind === 'file') {
      if (!onDocumentToFile) return 'documents cannot become files here'
      if (!m.id) return 'the document has no id'
      return null
    }
    if (m.kind === 'sourceFile') {
      if (m.project !== project || m.sourceId !== target.sourceId)
        return 'moving a file between checkouts is not built yet'
      if ((m.ref ?? null) !== (target.ref ?? null))
        return 'moving a file between worktrees is not built yet'
      if (parentOf(m.path) === target.dir) return 'the file is already here'
      if (!onMoveCheckoutFile) return 'files cannot move here'
      return null
    }
    if (m.kind === 'folder')
      return onFolderToFiles ? null : 'documents cannot become files here'
    if (m.kind === 'project') return 'a project cannot go inside a checkout'
    return 'a repo cannot go inside a checkout'
  }
  const sourceDropOn = (project: string, target: CodeDropTarget) => {
    const m = moving.current
    const refusal = sourceDropRefusal(project, target)
    moving.current = null
    if (!m || refusal !== null) return
    const branch = target.ref?.startsWith('~') ? target.ref.slice(1) : undefined
    // A name the checkout already has asks (3i), as a drop among documents
    // does. The checkout says where the free name is, since the window does
    // not hold its listing.
    const ask =
      (
        kind: 'document' | 'folder',
        rename: (renameTo: string) => void,
        merge?: () => void,
      ) =>
      (message: string, at?: { into: string; renameTo: string }) => {
        const renameTo = at?.renameTo ?? 'a free name'
        setCollision({
          kind,
          message,
          into: at?.into ?? message,
          renameTo,
          ...(merge ? { merge } : {}),
          rename: () => rename(renameTo),
        })
      }
    if (m.kind === 'file' && m.id) {
      const id = m.id
      const send = (opts: MoveOpts) =>
        onDocumentToFile?.(
          m.project,
          id,
          project,
          target.sourceId,
          target.dir,
          branch,
          opts,
        )
      send({ onCollision: ask('document', () => send({ rename: true })) })
    } else if (m.kind === 'sourceFile' && m.sourceId) {
      const sourceId = m.sourceId
      const send = (opts: MoveOpts) =>
        onMoveCheckoutFile?.(
          project,
          sourceId,
          m.path,
          target.dir,
          branch,
          opts,
        )
      send({ onCollision: ask('document', () => send({ rename: true })) })
    } else if (m.kind === 'folder') {
      // Rename writes the folder under the free name offered, and asks again
      // if that one is taken too; merge keeps both of each clashing file.
      const send = (as?: string, merge?: boolean) =>
        onFolderToFiles?.(
          m.project,
          m.path,
          project,
          target.sourceId,
          target.dir,
          branch,
          {
            ...(as ? { as } : {}),
            ...(merge ? { merge: true } : {}),
            onCollision: ask(
              'folder',
              (renameTo) => send(baseOf(renameTo)),
              () => send(as, true),
            ),
          },
        )
      send()
    }
  }
  /** What the code browser is handed for a project's checkout rows. */
  const codeDrag = (project: string) => ({
    dragFile: (
      file: { sourceId: string; path: string; ref: string | null },
      dragging: boolean,
    ) => {
      moving.current = dragging
        ? {
            project,
            kind: 'sourceFile',
            path: file.path,
            name: baseOf(file.path),
            sourceId: file.sourceId,
            ref: file.ref,
          }
        : null
    },
    dropIn: (target: CodeDropTarget): CodeMoveTarget => ({
      canDrop: () => sourceDropRefusal(project, target) === null,
      refusal: () => sourceDropRefusal(project, target),
      onDrop: () => sourceDropOn(project, target),
    }),
  })
  const commitFolderRename = () => {
    const edit = renamingFolder
    if (!edit) return
    setRenamingFolder(null)
    const to = renamedFolder(edit.path, edit.draft)
    if (!to || !canMoveFolder(edit.path, to, takenIn(edit.project))) {
      window.alert('That folder name is unavailable here')
      return
    }
    carryOpen(edit.project, edit.path, to)
    onMoveFolder?.(edit.project, edit.path, to)
  }
  /**
   * The folders a document can be sent to from its menu: the project's
   * top-level folders, as the tree shows them. Not `apps/`, which holds app
   * source rather than documents, and not every nested folder, which made a
   * list nobody could read. Deeper filing is a drag.
   */
  const moveChoices = (project: string, docPath: string) => {
    const paths = pathsIn(project)
    const folders = new Set<string>()
    for (const p of paths) {
      const parts = p.split('/')
      if (parts.length > 1 && parts[0] !== 'apps')
        folders.add(parts[0] as string)
    }
    const here = parentOf(docPath)
    return [
      ...(here ? [''] : []),
      ...[...folders]
        .filter((f) => f !== here)
        .sort((a, b) => a.localeCompare(b)),
    ]
  }

  /**
   * The open file's ancestors are always shown, whatever the default.
   *
   * Otherwise opening `apps/review-canvas/src/Diff.jsx` from ⌘T selects a row
   * inside two closed folders: the sidebar highlights nothing and the file
   * appears to have come from nowhere.
   */
  const openAncestors = useMemo(() => {
    if (!openFileId) return new Set<string>()
    const find = (
      list: TreeNode[],
      owner: string,
      ancestors: string[],
    ): Set<string> | null => {
      for (const node of list) {
        if (node.kind === 'file' && node.doc.id === openFileId)
          return new Set(ancestors)
        if (
          node.kind === 'item' &&
          ((node.item.type === 'skill' &&
            node.item.skill.documentId === openFileId) ||
            (node.item.type === 'standing' &&
              node.item.documentId === openFileId))
        )
          return new Set(ancestors)
        if (node.kind !== 'folder') continue
        const nextOwner = node.root ? node.path : owner
        const found = find(node.children, nextOwner, [
          ...ancestors,
          folderStateKey(nextOwner, node.path),
        ])
        if (found) return found
      }
      return null
    }
    return find(nodes, homeProjectId || projectId, []) ?? new Set<string>()
  }, [nodes, openFileId, homeProjectId, projectId])

  /** A PROJECT ROOT opens by default: it is the directory, not a drawer inside
   *  it, and a window on one project must still read as that project's files. */
  const rootPaths = useMemo(() => {
    const collect = (list: TreeNode[]): string[] =>
      list.flatMap((node) =>
        node.kind === 'folder'
          ? [
              ...(node.root ? [folderStateKey(node.path, node.path)] : []),
              ...collect(node.children),
            ]
          : [],
      )
    return new Set(collect(nodes))
  }, [nodes])

  /** Open because it is a project root, because it holds the open file, or
   *  because someone opened it. `toggled` flips whichever default applies. */
  const isOpen = (owner: string, path: string): boolean => {
    const key = folderStateKey(owner, path)
    // A project's Documents folder opens with it (RFC 0277 S12), so the tree
    // you had before projects held folders is still one row from the top.
    const byDefault = openAncestors.has(key) || rootPaths.has(key)
    return toggled.has(key) ? !byDefault : byDefault
  }

  if (empty) return <EmptyNote>No documents in this project yet.</EmptyNote>

  const render = (
    list: TreeNode[],
    depth: number,
    project: string,
    /** Inside a project's Documents folder, where rows move (RFC 0338). */
    docs = false,
  ): ReactNode[] => {
    // Two documents in one folder with one title read as one row twice: show
    // each one's file name, which is what tells them apart.
    const titles = new Map<string, number>()
    for (const node of list)
      if (node.kind === 'file')
        titles.set(node.doc.title, (titles.get(node.doc.title) ?? 0) + 1)
    const labelOf = (doc: { title: string; path: string }) =>
      (titles.get(doc.title) ?? 0) > 1
        ? `${doc.title} (${doc.path.slice(doc.path.lastIndexOf('/') + 1)})`
        : doc.title
    return list.flatMap((node) => {
      if (node.kind === 'item' && node.item.type === 'standing') {
        const { level, documentId } = node.item
        const owner = node.item.projectId ?? project ?? projectId
        return [
          <Row
            key={`${project}:${node.path}`}
            icon={<ScrollText size={13} />}
            label={node.name}
            title={
              level === 'world'
                ? 'Always on: every agent in every project reads this, every turn'
                : 'Always on: every agent in this project reads this, every turn'
            }
            depth={depth}
            active={!!documentId && documentId === openFileId}
            trailing={
              <span className="shrink-0 pr-2 text-[10px] text-muted-foreground/60">
                always on
              </span>
            }
            onClick={() => onOpenStanding?.(level, owner)}
          />,
        ]
      }
      if (node.kind === 'item' && node.item.type === 'skill') {
        const skill = node.item.skill
        const mod = isModSkill(skill)
        return [
          <Row
            key={`${project}:${node.path}`}
            icon={
              skill.appId ? (
                <AppIcon
                  appId={skill.appId}
                  size={12}
                  fallback={<AppWindow size={13} />}
                />
              ) : mod ? (
                <Sparkles size={13} />
              ) : (
                <FileText size={13} />
              )
            }
            label={skill.title}
            title={
              skill.shadows
                ? `/${skill.skillId}, replacing the ${skill.shadows.scope === 'mod' ? 'Mod' : 'World'} skill here`
                : `/${skill.skillId}`
            }
            depth={depth}
            active={!mod && skill.documentId === openFileId}
            trailing={
              skill.shadows ? (
                <span className="shrink-0 pr-2 text-[10px] text-muted-foreground/60">
                  replaces {skill.shadows.scope === 'mod' ? 'Mod' : 'World'}
                </span>
              ) : undefined
            }
            // Only World and project skills are rows, and each opens in the
            // editor (RFC 0334 3g).
            onClick={() => onOpen?.(skillAsFile(skill))}
            onSplit={
              !mod && onOpenSplit
                ? () => onOpenSplit(skillAsFile(skill))
                : undefined
            }
          />,
        ]
      }
      if (node.kind === 'item') {
        const it = node.item
        if (it.type !== 'session' && it.type !== 'app') return []
        const skill = it.type === 'app' ? appSkill?.(it.id) : undefined
        return [
          <Row
            key={`${project}:${node.path}`}
            icon={
              it.type === 'session' ? (
                <SessionMark id={it.id} size={13} />
              ) : (
                <AppWindow size={13} />
              )
            }
            label={node.name}
            title={
              it.type === 'session' && it.branch
                ? `${node.name} (${it.branch})`
                : node.name
            }
            depth={depth}
            active={it.type === 'app' && it.viewDocId === openFileId}
            trailing={
              it.type === 'session' && it.branch ? (
                <span className="max-w-[7rem] shrink-0 truncate pr-2 font-mono text-[10px] text-muted-foreground/60">
                  {it.branch}
                </span>
              ) : undefined
            }
            onClick={() =>
              it.type === 'session'
                ? onOpenSession?.(it.id)
                : onOpenApp?.(it, project)
            }
            {...(skill
              ? {
                  trailing: (
                    <ItemMenu
                      label={`${node.name} actions`}
                      open={menuFor === node.path}
                      onOpenChange={(next) => {
                        setMenuFor(next ? node.path : null)
                        setContextMenu(null)
                      }}
                      position={
                        contextMenu?.id === node.path
                          ? { x: contextMenu.x, y: contextMenu.y }
                          : undefined
                      }
                      items={[
                        {
                          label: 'Open skill',
                          onSelect: () => onOpen?.(skillAsFile(skill)),
                        },
                      ]}
                    />
                  ),
                  onContextMenu: (event: ReactMouseEvent<HTMLDivElement>) => {
                    event.preventDefault()
                    event.stopPropagation()
                    setMenuFor(node.path)
                    setContextMenu({
                      id: node.path,
                      x: event.clientX,
                      y: event.clientY,
                    })
                  },
                }
              : {})}
          />,
        ]
      }
      if (node.kind === 'file') {
        const Icon = fileIconFor(node.doc.mime)
        const edit =
          renaming?.id === node.doc.id && renaming.project === project
            ? renaming
            : null
        const menuKey = `file:${project}:${node.doc.id}`
        const choices = docs ? moveChoices(project, node.doc.path) : []
        const fileMenu =
          docs &&
          (onRename || onMoveDocument || onDeleteDocument || onMoveToProject)
            ? {
                trailing: (
                  <ItemMenu
                    label={`${node.doc.title} actions`}
                    open={menuFor === menuKey}
                    onOpenChange={(next) => {
                      setMenuFor(next ? menuKey : null)
                      setContextMenu(null)
                    }}
                    position={
                      contextMenu?.id === menuKey
                        ? { x: contextMenu.x, y: contextMenu.y }
                        : undefined
                    }
                    items={[
                      ...(onRename
                        ? [
                            {
                              label: 'Rename',
                              onSelect: () =>
                                setRenaming({
                                  id: node.doc.id,
                                  draft: node.doc.title,
                                  project,
                                  path: node.doc.path,
                                }),
                            },
                          ]
                        : []),
                      ...(onMoveDocument && choices.length > 0
                        ? [
                            {
                              label: 'Move to',
                              children: choices.map((folder) => ({
                                label: folder || 'Documents (top level)',
                                onSelect: () =>
                                  moveDocumentTo(
                                    project,
                                    node.doc.id,
                                    node.doc.path,
                                    folder,
                                  ),
                              })),
                            },
                          ]
                        : []),
                      ...(onMoveToProject && projectNames.size > 1
                        ? [
                            {
                              label: 'Move to project',
                              children: [...projectNames]
                                .filter(([id]) => id !== project)
                                .map(([id, name]) => ({
                                  label: name || id,
                                  onSelect: () =>
                                    moveDocumentAcross(
                                      project,
                                      id,
                                      node.doc.id,
                                      node.doc.path,
                                      parentOf(node.doc.path),
                                    ),
                                })),
                            },
                          ]
                        : []),
                      ...(onDeleteDocument
                        ? [
                            {
                              label: 'Delete',
                              confirm: 'Click again to delete',
                              danger: true,
                              onSelect: () =>
                                onDeleteDocument(project, node.doc.id),
                            },
                          ]
                        : []),
                    ]}
                  />
                ),
                onContextMenu: (event: ReactMouseEvent<HTMLDivElement>) => {
                  event.preventDefault()
                  event.stopPropagation()
                  setMenuFor(menuKey)
                  setContextMenu({
                    id: menuKey,
                    x: event.clientX,
                    y: event.clientY,
                  })
                },
              }
            : {}
        return [
          <Row
            key={`doc:${project}:${node.doc.id}`}
            // A record of an app draws that app's tile, so a folder of a
            // canvas, a deck and a task list reads as three apps (RFC 0332).
            icon={
              <AppIcon
                dataType={node.doc.dataType || undefined}
                mime={node.doc.mime}
                size={12}
                fallback={<Icon size={13} />}
              />
            }
            label={labelOf(node.doc)}
            title={node.doc.path}
            depth={depth}
            active={node.doc.id === openFileId}
            onClick={() => onOpen?.(docAsFile(node.doc))}
            onSplit={
              onOpenSplit ? () => onOpenSplit(docAsFile(node.doc)) : undefined
            }
            onDoubleClick={
              onRename
                ? () =>
                    setRenaming({
                      id: node.doc.id,
                      draft: node.doc.title,
                      project,
                      path: node.doc.path,
                    })
                : undefined
            }
            editing={
              edit
                ? {
                    draft: edit.draft,
                    setDraft: (draft) => setRenaming({ ...edit, draft }),
                    commit: () => commitRename(node.doc.title),
                    cancel: () => setRenaming(null),
                  }
                : undefined
            }
            {...(docs && onMoveDocument
              ? {
                  moveDrag: (dragging: boolean) => {
                    moving.current = dragging
                      ? {
                          project,
                          kind: 'file',
                          path: node.doc.path,
                          id: node.doc.id,
                        }
                      : null
                  },
                }
              : {})}
            // A drop on a document means its folder: aiming for a folder row
            // between documents is a gesture nobody makes on purpose.
            {...(docs && (onMoveDocument || onMoveFolder)
              ? {
                  moveTarget: {
                    canDrop: () => canDropOn(project, parentOf(node.doc.path)),
                    refusal: () =>
                      dropRefusal(project, parentOf(node.doc.path)),
                    onDrop: () => dropOn(project, parentOf(node.doc.path)),
                  },
                }
              : {})}
            {...fileMenu}
          />,
        ]
      }
      const owner = node.root ? node.path : project
      const isCollapsed = !isOpen(owner, node.path)
      // The directory owns the folder row. The code browser contributes its
      // source actions and lazy file children to this one tree.
      if (node.source === 'code')
        return [
          <Fragment key={`code:${project}:${node.path}`}>
            {renderCode?.(
              project,
              depth,
              {
                open: !isCollapsed,
                toggle: () => toggle(owner, node.path),
                dragSource: (sourceId, dragging) => {
                  moving.current = dragging
                    ? {
                        project,
                        kind: 'source',
                        id: sourceId,
                        path: node.path,
                        name: node.name,
                      }
                    : null
                },
                ...(onDocumentToFile || onFileToDocument
                  ? codeDrag(project)
                  : {}),
                renderSourceRow: ({ sourceId, actions, trailing, ...row }) => (
                  <Row
                    key={`folder:${project}:${node.path}`}
                    {...row}
                    trailing={
                      <span className="flex shrink-0 items-center">
                        {trailing}
                        {actions.length ? (
                          <ItemMenu
                            label={`${node.name} source actions`}
                            open={menuFor === `source:${project}:${sourceId}`}
                            onOpenChange={(next) => {
                              setMenuFor(
                                next ? `source:${project}:${sourceId}` : null,
                              )
                              setContextMenu(null)
                            }}
                            position={
                              contextMenu?.id ===
                              `source:${project}:${sourceId}`
                                ? { x: contextMenu.x, y: contextMenu.y }
                                : undefined
                            }
                            items={actions}
                          />
                        ) : null}
                      </span>
                    }
                    onContextMenu={
                      actions.length
                        ? (event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            const key = `source:${project}:${sourceId}`
                            setMenuFor(key)
                            setContextMenu({
                              id: key,
                              x: event.clientX,
                              y: event.clientY,
                            })
                          }
                        : undefined
                    }
                    tone="folder"
                    moveDrag={
                      onPlaceSource
                        ? (dragging) => {
                            moving.current = dragging
                              ? {
                                  project,
                                  kind: 'source',
                                  id: sourceId,
                                  path: node.path,
                                  name: node.name,
                                }
                              : null
                          }
                        : undefined
                    }
                  />
                ),
              },
              node.sourceId,
              node.sourceId ? node.name : undefined,
            )}
          </Fragment>,
        ]
      const projectEdit =
        node.root && renamingProject?.id === node.path ? renamingProject : null
      // RFC 0338: a folder of documents renames, drags, and takes drops; the
      // Documents folder itself takes drops, meaning "to the top".
      const docFolder = docs && !node.root && !node.source
      const folderEdit =
        docFolder &&
        renamingFolder?.project === project &&
        renamingFolder.path === node.path
          ? renamingFolder
          : null
      const folderKey = `folder:${project}:${node.path}`
      // A project's own row takes a drop too, meaning its Documents' top
      // level: a project with no documents draws no Documents folder, so
      // without this an empty project could never receive a move.
      const dropFolder = docFolder ? node.path : node.root ? '' : null
      const dropProject = node.root ? node.path : project
      const projectTasks = node.root
        ? (tasksByProject.get(node.path) ?? [])
        : []
      return [
        <Row
          // render() flattens every project's rows into one sibling list.
          key={`folder:${project}:${node.path}`}
          // A chevron alone: a folder icon beside it says the same thing twice.
          icon={
            isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />
          }
          label={node.name}
          tone={node.root ? 'project' : node.source ? 'folder' : undefined}
          // A project's own folders are named, not pathed: `<id>/:documents`
          // is a key, not something a person reads.
          title={node.source ? node.name : node.path}
          depth={depth}
          trailing={
            <span className="flex shrink-0 items-center">
              {node.root && (
                <ShareTarget documentId={node.path} kind="project" />
              )}
              {/* The project's own actions. A menu rather than more hover
                  buttons: renaming a project is rare enough that it should
                  not spend room in every row, and the row's click already
                  means "collapse this". */}
              {docFolder && onMoveFolder ? (
                <ItemMenu
                  label={`${node.name} folder actions`}
                  open={menuFor === folderKey}
                  onOpenChange={(next) => {
                    setMenuFor(next ? folderKey : null)
                    setContextMenu(null)
                  }}
                  position={
                    contextMenu?.id === folderKey
                      ? { x: contextMenu.x, y: contextMenu.y }
                      : undefined
                  }
                  items={[
                    ...(onNewFolder
                      ? [
                          {
                            label: 'New folder',
                            onSelect: () => onNewFolder(project, node.path),
                          },
                        ]
                      : []),
                    ...(onAttachSource
                      ? [
                          {
                            label: 'Attach repository or folder',
                            onSelect: () => onAttachSource(project, node.path),
                          },
                        ]
                      : []),
                    ...(onMakeProject
                      ? [
                          {
                            label: 'Make project',
                            onSelect: () => onMakeProject(project, node.path),
                          },
                        ]
                      : []),
                    ...(onPlaceSource && sourcesIn?.(project).length
                      ? [
                          {
                            label: 'Place source here',
                            children: sourcesIn(project).map((source) => ({
                              label: source.name,
                              onSelect: () =>
                                onPlaceSource(
                                  project,
                                  source.id,
                                  `${node.path}/${source.name.replace(/\//g, '-')}`,
                                ),
                            })),
                          },
                        ]
                      : []),
                    {
                      label: 'Rename folder',
                      onSelect: () =>
                        setRenamingFolder({
                          project,
                          path: node.path,
                          draft: node.name,
                        }),
                    },
                    ...(node.explicitId && onRemoveFolder
                      ? [
                          {
                            label: 'Remove empty folder',
                            onSelect: () => onRemoveFolder(project, node.path),
                          },
                        ]
                      : []),
                  ]}
                />
              ) : null}
              {/* RFC 0313 3j: the Mod branch this window reads here. */}
              {node.root ? (
                <BranchChip
                  projectId={node.path}
                  {...(onOpen
                    ? {
                        onOpen: (f) => onOpen(f),
                        // RFC 0313 3i: History lives in the branch menu.
                        onOpenHistory: () => onOpen(historySubject(node.path)),
                      }
                    : {})}
                />
              ) : null}
              {node.root &&
              (onRenameProject || onDeleteProject || onCreateProject) ? (
                <ProjectMenu
                  projectId={node.path}
                  open={menuFor === node.path}
                  onOpenChange={(next) => {
                    setMenuFor(next ? node.path : null)
                    setContextMenu(null)
                  }}
                  position={
                    contextMenu?.id === node.path
                      ? { x: contextMenu.x, y: contextMenu.y }
                      : undefined
                  }
                  onRename={
                    onRenameProject
                      ? () =>
                          setRenamingProject({
                            id: node.path,
                            draft: node.name,
                          })
                      : undefined
                  }
                  onDelete={
                    onDeleteProject
                      ? () => onDeleteProject(node.path)
                      : undefined
                  }
                  {...(onDissolveProject && canDissolveProject?.(node.path)
                    ? { onDissolve: () => onDissolveProject(node.path) }
                    : {})}
                  {...(onCreateProject ? { onCreate: onCreateProject } : {})}
                  {...(onNewFolder
                    ? { onNewFolder: () => onNewFolder(node.path, '') }
                    : {})}
                  {...(onAttachSource
                    ? {
                        onAttachSource: () => onAttachSource(node.path, ''),
                      }
                    : {})}
                />
              ) : null}
              {/* Open tasks for a project root (RFC 0319 3d). Muted and
                  on the right: something you read when you look, not a second
                  label competing with the project's name. */}
              {node.count ? (
                <TaskChip
                  tasks={projectTasks}
                  port={taskPortsByProject.get(node.path)}
                  label={String(node.count.open)}
                  ariaLabel={`${node.count.open} open project ${node.count.open === 1 ? 'task' : 'tasks'}${node.count.waiting ? `, ${node.count.waiting} waiting on you` : ''}`}
                  attention={node.count.waiting > 0}
                  includeCompleted
                  placement="right-start"
                  className="shrink-0 text-[10px] tabular-nums"
                />
              ) : null}
            </span>
          }
          onClick={() => toggle(owner, node.path)}
          onContextMenu={
            node.root || (docFolder && onMoveFolder)
              ? (event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  const key = node.root ? node.path : folderKey
                  setMenuFor(key)
                  setContextMenu({
                    id: key,
                    x: event.clientX,
                    y: event.clientY,
                  })
                }
              : undefined
          }
          {...(docFolder && onMoveFolder
            ? {
                moveDrag: (dragging: boolean) => {
                  moving.current = dragging
                    ? { project, kind: 'folder', path: node.path }
                    : null
                },
              }
            : {})}
          {...(node.root && onMoveProject
            ? {
                moveDrag: (dragging: boolean) => {
                  setDraggingProject(dragging)
                  moving.current = dragging
                    ? {
                        project: node.path,
                        kind: 'project',
                        id: node.path,
                        path: node.name.replace(/\//g, '-'),
                      }
                    : null
                },
              }
            : {})}
          {...(dropFolder !== null && (onMoveDocument || onMoveFolder)
            ? {
                moveTarget: {
                  canDrop: () => canDropOn(dropProject, dropFolder),
                  refusal: () => dropRefusal(dropProject, dropFolder),
                  onDrop: () => dropOn(dropProject, dropFolder),
                },
              }
            : {})}
          onDoubleClick={
            node.root && onRenameProject
              ? () => setRenamingProject({ id: node.path, draft: node.name })
              : docFolder && onMoveFolder
                ? () =>
                    setRenamingFolder({
                      project,
                      path: node.path,
                      draft: node.name,
                    })
                : undefined
          }
          editing={
            projectEdit
              ? {
                  draft: projectEdit.draft,
                  setDraft: (draft) =>
                    setRenamingProject({ id: node.path, draft }),
                  commit: () => commitProjectRename(node.name),
                  cancel: () => setRenamingProject(null),
                }
              : folderEdit
                ? {
                    draft: folderEdit.draft,
                    setDraft: (draft) =>
                      setRenamingFolder({ ...folderEdit, draft }),
                    commit: commitFolderRename,
                    cancel: () => setRenamingFolder(null),
                  }
                : undefined
          }
        />,
        ...(newFolderSlot &&
        (node.root
          ? newFolderSlot.owner === node.path && !newFolderSlot.parentPath
          : newFolderSlot.owner === project &&
            newFolderSlot.parentPath === node.path)
          ? [<Fragment key={`new:${folderKey}`}>{newFolderSlot.form}</Fragment>]
          : []),
        ...(isCollapsed
          ? []
          : render(
              node.children,
              depth + 1,
              node.root ? node.path : project,
              docs || !!node.root,
            )),
      ]
    })
  }

  return (
    <>
      {homeProjectId && draggingProject ? (
        <div className="sticky top-0 z-10 bg-background">
          <Row
            icon={<ChevronUp size={13} />}
            label="Top level"
            title="Drop a project here to move it to the top level"
            moveTarget={{
              canDrop: () =>
                moving.current?.kind === 'project' && !!moving.current.id,
              onDrop: () => dropOn(homeProjectId, ''),
            }}
          />
        </div>
      ) : null}
      {render(nodes, 0, homeProjectId || projectId, !!homeProjectId)}
      {collision ? (
        <div
          role="dialog"
          aria-label={`${COLLISION_NOUN[collision.kind]} already there`}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
        >
          <div className="w-80 rounded-lg border border-border bg-background p-4 text-[13px] shadow-lg">
            <p className="font-medium">
              {collision.within
                ? 'Two documents in this folder share a name'
                : `${collision.into} is already there`}
            </p>
            <p className="mt-1 text-muted-foreground">
              {collision.within
                ? 'Keep both moves them with the second under a free name, like skill-2.md.'
                : collision.merge
                  ? `Merge keeps both copies of anything with the same name. Rename moves this one beside it as ${collision.renameTo}.`
                  : `Rename moves this ${COLLISION_NOUN[collision.kind].toLowerCase()} beside it as ${collision.renameTo}.`}
            </p>
            {import.meta.env.DEV ? (
              <p className="mt-2 break-words text-muted-foreground text-xs">
                {collision.message}
              </p>
            ) : null}
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                className="rounded px-2 py-1 hover:bg-muted"
                onClick={() => setCollision(null)}
              >
                Cancel
              </button>
              {collision.within ? null : (
                <button
                  type="button"
                  className={
                    collision.merge
                      ? 'rounded px-2 py-1 hover:bg-muted'
                      : 'rounded bg-foreground px-2 py-1 text-background'
                  }
                  onClick={() => {
                    const { rename } = collision
                    setCollision(null)
                    rename()
                  }}
                >
                  Rename
                </button>
              )}
              {collision.merge ? (
                <button
                  type="button"
                  className="rounded bg-foreground px-2 py-1 text-background"
                  onClick={() => {
                    const { merge } = collision
                    setCollision(null)
                    merge?.()
                  }}
                >
                  {collision.within ? 'Keep both' : 'Merge'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
